import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, TrendingUp, DollarSign, Calendar, FileText, Eye, Download, FileSpreadsheet, GitCompare, CheckSquare, Square, Search, X, Clock } from 'lucide-react';
import { formatNumberES } from '@/lib/utils';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart, ReferenceLine } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#11998e', '#38ef7d'];

export const Proyecciones = () => {
  const { API, token } = useContext(AuthContext);
  const [proyecciones, setProyecciones] = useState([]);
  const [productos, setProductos] = useState([]);
  const [proyeccionModalOpen, setProyeccionModalOpen] = useState(false);
  const [verProyeccionModalOpen, setVerProyeccionModalOpen] = useState(false);
  const [compararModalOpen, setCompararModalOpen] = useState(false);
  const [proyeccionesSeleccionadas, setProyeccionesSeleccionadas] = useState([]);
  const [proyeccionesComparadas, setProyeccionesComparadas] = useState([]);
  const [editandoProyeccion, setEditandoProyeccion] = useState(null);
  const [proyeccionCalculada, setProyeccionCalculada] = useState(null);
  const [totalesProyecciones, setTotalesProyecciones] = useState({}); // Cache de totales por proyección
  const [loading, setLoading] = useState(false);
  const [comparando, setComparando] = useState(false);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroMeses, setFiltroMeses] = useState('');
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    meses_proyectados: 12,
    unidades_por_mes: {}
  });

  useEffect(() => {
    cargarProyecciones();
    cargarProductos();
  }, []);

  const cargarProyecciones = async () => {
    try {
      const response = await axios.get(`${API}/proyecciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProyecciones(response.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Tu cuenta está inactiva');
        // Redirigir al login después de un momento
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar proyecciones';
        toast.error(errorMessage);
      }
    }
  };

  const cargarProductos = async () => {
    try {
      const response = await axios.get(`${API}/productos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductos(response.data);
      
      // Inicializar unidades_por_mes con arrays vacíos para cada producto
      const unidadesIniciales = {};
      response.data.forEach(producto => {
        unidadesIniciales[producto.id] = Array(12).fill(0);
      });
      setFormData(prev => ({
        ...prev,
        unidades_por_mes: unidadesIniciales
      }));
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const resetForm = () => {
    const unidadesIniciales = {};
    productos.forEach(producto => {
      unidadesIniciales[producto.id] = Array(12).fill(0);
    });
    setFormData({
      nombre: '',
      descripcion: '',
      fecha_inicio: new Date().toISOString().split('T')[0],
      meses_proyectados: 12,
      unidades_por_mes: unidadesIniciales
    });
    setEditandoProyeccion(null);
  };

  const abrirModalProyeccion = (proyeccion = null) => {
    if (proyeccion) {
      // Convertir fecha_inicio a formato YYYY-MM-DD
      const fechaInicio = new Date(proyeccion.fecha_inicio).toISOString().split('T')[0];
      setFormData({
        nombre: proyeccion.nombre,
        descripcion: proyeccion.descripcion || '',
        fecha_inicio: fechaInicio,
        meses_proyectados: proyeccion.meses_proyectados,
        unidades_por_mes: proyeccion.unidades_por_mes || {}
      });
      setEditandoProyeccion(proyeccion.id);
    } else {
      resetForm();
      setEditandoProyeccion(null);
    }
    setProyeccionModalOpen(true);
  };

  const cerrarModalProyeccion = () => {
    setProyeccionModalOpen(false);
    resetForm();
  };

  const guardarProyeccion = async () => {
    if (!formData.nombre || !formData.fecha_inicio) {
      toast.error('Completa el nombre y la fecha de inicio');
      return;
    }

    // Asegurar que unidades_por_mes tenga el número correcto de meses
    const unidadesAjustadas = {};
    Object.keys(formData.unidades_por_mes).forEach(productoId => {
      const unidades = formData.unidades_por_mes[productoId] || [];
      unidadesAjustadas[productoId] = unidades.slice(0, formData.meses_proyectados);
    });

    const datosProyeccion = {
      ...formData,
      unidades_por_mes: unidadesAjustadas,
      fecha_inicio: new Date(formData.fecha_inicio).toISOString()
    };

    try {
      if (editandoProyeccion) {
        await axios.put(`${API}/proyecciones/${editandoProyeccion}`, datosProyeccion, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Limpiar cache de totales al editar
        setTotalesProyecciones(prev => {
          const nuevo = { ...prev };
          delete nuevo[editandoProyeccion];
          return nuevo;
        });
        toast.success('Proyección actualizada');
      } else {
        await axios.post(`${API}/proyecciones`, datosProyeccion, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proyección creada');
      }
      cargarProyecciones();
      cerrarModalProyeccion();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proyección');
    }
  };

  const eliminarProyeccion = async (id) => {
    if (!window.confirm('¿Eliminar esta proyección?')) return;
    try {
      await axios.delete(`${API}/proyecciones/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Limpiar cache de totales al eliminar
      setTotalesProyecciones(prev => {
        const nuevo = { ...prev };
        delete nuevo[id];
        return nuevo;
      });
      toast.success('Proyección eliminada');
      cargarProyecciones();
    } catch (error) {
      toast.error('Error al eliminar proyección');
    }
  };

  const calcularProyeccion = async (id) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/proyecciones/${id}/calcular`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProyeccionCalculada(response.data);
      
      // Almacenar totales en cache para mostrar en el listado
      if (response.data.totales) {
        setTotalesProyecciones(prev => ({
          ...prev,
          [id]: response.data.totales
        }));
      }
      
      setVerProyeccionModalOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al calcular proyección');
    } finally {
      setLoading(false);
    }
  };

  const toggleSeleccionProyeccion = (id) => {
    setProyeccionesSeleccionadas(prev => {
      if (prev.includes(id)) {
        return prev.filter(pId => pId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const compararProyecciones = async () => {
    if (proyeccionesSeleccionadas.length < 2) {
      toast.error('Selecciona al menos 2 proyecciones para comparar');
      return;
    }

    setComparando(true);
    try {
      const promesas = proyeccionesSeleccionadas.map(id =>
        axios.post(`${API}/proyecciones/${id}/calcular`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      const resultados = await Promise.all(promesas);
      setProyeccionesComparadas(resultados.map(r => r.data));
      setCompararModalOpen(true);
    } catch (error) {
      toast.error('Error al calcular proyecciones para comparar');
    } finally {
      setComparando(false);
    }
  };

  const cerrarCompararModal = () => {
    setCompararModalOpen(false);
    setProyeccionesSeleccionadas([]);
    setProyeccionesComparadas([]);
  };

  const actualizarUnidadesMes = (productoId, mesIndex, valor) => {
    const unidades = formData.unidades_por_mes[productoId] || [];
    const nuevasUnidades = [...unidades];
    nuevasUnidades[mesIndex] = parseFloat(valor) || 0;
    
    setFormData({
      ...formData,
      unidades_por_mes: {
        ...formData.unidades_por_mes,
        [productoId]: nuevasUnidades
      }
    });
  };

  // Preparar datos para gráficos con mes y año
  const datosGraficoIngresos = proyeccionCalculada?.meses.map(m => ({
    mes: `${m.nombre_mes || 'Mes'} ${m.año_calendario || m.mes}`,
    mesNum: m.mes,
    ingresos: m.ingresos,
    costes: m.costes_variables + m.gastos_fijos,
    beneficio: m.beneficio_neto
  })) || [];

  const datosGraficoTesoreria = proyeccionCalculada?.tesoreria.map(t => ({
    mes: `${t.nombre_mes || 'Mes'} ${t.año_calendario || t.mes}`,
    mesNum: t.mes,
    cobros: t.cobros_mes,
    pagos: t.pagos_mes,
    flujo: t.flujo_caja,
    saldo: t.saldo_acumulado
  })) || [];

  const nombresMeses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  // Calcular métricas resumidas
  const calcularMetricas = () => {
    if (!proyeccionCalculada || !proyeccionCalculada.meses || proyeccionCalculada.meses.length === 0) {
      return null;
    }

    const meses = proyeccionCalculada.meses.length;
    const totales = proyeccionCalculada.totales;
    const tesoreria = proyeccionCalculada.tesoreria;

    const horasTotal = totales.horas_trabajadas_total || 0;
    const horasPromedioMensual = horasTotal / meses;
    const horasMaximas = proyeccionCalculada.meses.length > 0
      ? Math.max(...proyeccionCalculada.meses.map(m => m.horas_trabajadas || 0))
      : 0;
    const horasMinimas = proyeccionCalculada.meses.length > 0
      ? Math.min(...proyeccionCalculada.meses.map(m => m.horas_trabajadas || 0))
      : 0;
    const precioVentaPorHora = horasTotal > 0 
      ? totales.ingresos_total / horasTotal 
      : 0;
    const beneficioPorHora = horasTotal > 0 
      ? totales.beneficio_neto_total / horasTotal 
      : 0;

    return {
      ingresosPromedioMensual: totales.ingresos_total / meses,
      costesPromedioMensual: (totales.costes_variables_total + totales.gastos_fijos_total) / meses,
      beneficioPromedioMensual: totales.beneficio_neto_total / meses,
      flujoCajaPromedioMensual: totales.flujo_caja_total / meses,
      margenBruto: totales.ingresos_total > 0 
        ? ((totales.beneficio_neto_total / totales.ingresos_total) * 100) 
        : 0,
      mesesPositivos: proyeccionCalculada.meses.filter(m => m.beneficio_neto > 0).length,
      mesesNegativos: proyeccionCalculada.meses.filter(m => m.beneficio_neto <= 0).length,
      saldoFinal: tesoreria && tesoreria.length > 0 
        ? tesoreria[tesoreria.length - 1].saldo_acumulado 
        : 0,
      saldoMaximo: tesoreria && tesoreria.length > 0
        ? Math.max(...tesoreria.map(t => t.saldo_acumulado))
        : 0,
      saldoMinimo: tesoreria && tesoreria.length > 0
        ? Math.min(...tesoreria.map(t => t.saldo_acumulado))
        : 0,
      horasTotal: horasTotal,
      horasPromedioMensual: horasPromedioMensual,
      horasMaximas: horasMaximas,
      horasMinimas: horasMinimas,
      precioVentaPorHora: precioVentaPorHora,
      beneficioPorHora: beneficioPorHora
    };
  };

  const metricas = calcularMetricas();

  // Función para exportar a PDF
  const exportarPDF = async () => {
    if (!proyeccionCalculada) return;

    toast.info('Generando PDF...', { duration: 2000 });

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // Título
    doc.setFontSize(18);
    doc.text(proyeccionCalculada.proyeccion.nombre, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    doc.setFontSize(12);
    doc.text(`Fecha de inicio: ${new Date(proyeccionCalculada.proyeccion.fecha_inicio).toLocaleDateString('es-ES')}`, pageWidth / 2, yPos, { align: 'center' });
    doc.text(`Meses proyectados: ${proyeccionCalculada.proyeccion.meses_proyectados}`, pageWidth / 2, yPos + 5, { align: 'center' });
    yPos += 15;

    // Resumen de totales
    doc.setFontSize(14);
    doc.text('Resumen de Totales', 14, yPos);
    yPos += 8;

    doc.setFontSize(10);
    const totalesData = [
      ['Concepto', 'Total'],
      ['Ingresos Total', `${formatNumberES(proyeccionCalculada.totales.ingresos_total)}€`],
      ['Costes Variables', `${formatNumberES(proyeccionCalculada.totales.costes_variables_total)}€`],
      ['Gastos Fijos', `${formatNumberES(proyeccionCalculada.totales.gastos_fijos_total)}€`],
      ['Beneficio Neto', `${formatNumberES(proyeccionCalculada.totales.beneficio_neto_total)}€`],
      ['Flujo de Caja Total', `${formatNumberES(proyeccionCalculada.totales.flujo_caja_total)}€`]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [totalesData[0]],
      body: totalesData.slice(1),
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] }
    });
    yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;

    // Métricas
    if (metricas) {
      doc.setFontSize(14);
      doc.text('Métricas Resumidas', 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      const metricasData = [
        ['Concepto', 'Valor'],
        ['Ingresos Promedio Mensual', `${formatNumberES(metricas.ingresosPromedioMensual)}€`],
        ['Costes Promedio Mensual', `${formatNumberES(metricas.costesPromedioMensual)}€`],
        ['Beneficio Promedio Mensual', `${formatNumberES(metricas.beneficioPromedioMensual)}€`],
        ['Flujo Caja Promedio Mensual', `${formatNumberES(metricas.flujoCajaPromedioMensual)}€`],
        ['Margen Bruto (%)', `${metricas.margenBruto.toFixed(2)}%`],
        ['Meses con Beneficio Positivo', `${metricas.mesesPositivos}`],
        ['Meses con Beneficio Negativo', `${metricas.mesesNegativos}`],
        ['Saldo Final', `${formatNumberES(metricas.saldoFinal)}€`],
        ['Saldo Máximo', `${formatNumberES(metricas.saldoMaximo)}€`],
        ['Saldo Mínimo', `${formatNumberES(metricas.saldoMinimo)}€`]
      ];

      autoTable(doc, {
        startY: yPos,
        head: [metricasData[0]],
        body: metricasData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [102, 126, 234] }
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;
    }

    // Análisis de Horas Trabajadas
    if (metricas && metricas.horasTotal > 0) {
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Análisis de Horas Trabajadas', 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      const horasData = [
        ['Concepto', 'Valor'],
        ['Horas Totales', `${formatNumberES(metricas.horasTotal, 1)}h`],
        ['Promedio Mensual', `${formatNumberES(metricas.horasPromedioMensual, 1)}h`],
        ['Máximo Mensual', `${formatNumberES(metricas.horasMaximas, 1)}h`],
        ['Mínimo Mensual', `${formatNumberES(metricas.horasMinimas, 1)}h`],
        ['PVP por Hora (sin IVA)', `${formatNumberES(metricas.precioVentaPorHora)}€/h`],
        ['Beneficio por Hora', `${formatNumberES(metricas.beneficioPorHora)}€/h`]
      ];

      autoTable(doc, {
        startY: yPos,
        head: [horasData[0]],
        body: horasData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [249, 115, 22] }
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;
    }

    // Tabla detallada mes a mes (transpuesta: conceptos como filas) - Página apaisada
    // Crear nueva página en formato landscape usando el método correcto de jsPDF
    doc.addPage('a4', 'l'); // 'l' = landscape
    
    yPos = 20;
    // Obtener dimensiones reales de la página landscape
    const landscapeWidth = doc.internal.pageSize.getWidth();
    const landscapeHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(14);
    doc.text('Detalle Mes a Mes (€)', 14, yPos);
    yPos += 8;

    // Preparar datos transpuestos: conceptos como filas, meses como columnas
    const conceptos = [
      { label: 'Ingresos', getValue: (mes) => mes.ingresos, color: 'green' },
      { label: 'Costes Var.', getValue: (mes) => mes.costes_variables, color: 'red' },
      { label: 'Beneficio Bruto', getValue: (mes) => mes.beneficio_bruto || (mes.ingresos - mes.costes_variables), color: 'gray', bold: true },
      { label: 'Gastos Fijos', getValue: (mes) => mes.gastos_fijos, color: 'red' },
      { label: 'IVA No Ded.', getValue: (mes) => mes.iva_no_deducible || 0, color: 'orange' },
      { label: 'Benef. Antes Imp.', getValue: (mes) => mes.beneficio_antes_impuestos || mes.beneficio_neto, color: 'blue', bold: true },
      { label: 'Impuestos', getValue: (mes) => mes.impuestos_directos || 0, color: 'red' },
      { label: 'Beneficio Neto', getValue: (mes) => mes.beneficio_neto, color: 'green', bold: true },
      { label: 'Cobros', getValue: (mes, idx) => proyeccionCalculada.tesoreria[idx].cobros_mes, color: 'green' },
      { label: 'Pagos', getValue: (mes, idx) => proyeccionCalculada.tesoreria[idx].pagos_mes, color: 'red' },
      { label: 'Flujo Caja', getValue: (mes, idx) => proyeccionCalculada.tesoreria[idx].flujo_caja, color: 'gray' },
      { label: 'Saldo Acum.', getValue: (mes, idx) => proyeccionCalculada.tesoreria[idx].saldo_acumulado, color: 'purple', bold: true }
    ];

    // Encabezados: Concepto + meses
    const headerRow = ['Concepto', ...proyeccionCalculada.meses.map((mes) => {
      return mes.nombre_mes && mes.año_calendario 
        ? `${mes.nombre_mes} ${mes.año_calendario}` 
        : `Mes ${mes.mes}`;
    })];

    // Filas de datos (sin símbolo €)
    const bodyRows = conceptos.map(concepto => [
      concepto.label,
      ...proyeccionCalculada.meses.map((mes, idx) => {
        const valor = concepto.getValue(mes, idx);
        return formatNumberES(valor);
      })
    ]);

    // Calcular anchos: columna concepto más estrecha, resto distribuidas equitativamente
    const numMeses = proyeccionCalculada.meses.length;
    const margin = 14;
    const availableWidth = landscapeWidth - (margin * 2);
    const conceptoWidth = 35; // Más espacio para concepto en landscape
    const mesesWidth = (availableWidth - conceptoWidth) / numMeses;

    // Crear objeto de estilos de columna
    const columnStyles = {
      0: { cellWidth: conceptoWidth, fontStyle: 'bold' }
    };
    
    // Aplicar ancho a todas las columnas de meses
    for (let i = 1; i <= numMeses; i++) {
      columnStyles[i] = { cellWidth: mesesWidth };
    }

    autoTable(doc, {
      startY: yPos,
      head: [headerRow],
      body: bodyRows,
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] },
      styles: { fontSize: 9 }, // Aumentado de 7 a 9
      margin: { left: margin, right: margin },
      columnStyles: columnStyles
    });
    
    // Volver a formato portrait para las siguientes páginas
    doc.addPage('a4', 'p'); // 'p' = portrait
    yPos = 20;

    // Liquidaciones de IVA
    if (proyeccionCalculada.liquidaciones_iva && proyeccionCalculada.liquidaciones_iva.length > 0) {
      // Nueva página si es necesario
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Liquidaciones Trimestrales de IVA', 14, yPos);
      yPos += 8;

      const liquidacionesIVAData = [
        ['Trimestre', 'IVA Repercutido', 'IVA Soportado', 'IVA a Pagar', 'Fecha Vencimiento']
      ];

      proyeccionCalculada.liquidaciones_iva.forEach(liq => {
        liquidacionesIVAData.push([
          `T${liq.trimestre} ${liq.año}`,
          `${formatNumberES(liq.iva_repercutido)}€`,
          `${formatNumberES(liq.iva_soportado)}€`,
          `${formatNumberES(liq.iva_a_pagar)}€`,
          new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')
        ]);
      });

      autoTable(doc, {
        startY: yPos,
        head: [liquidacionesIVAData[0]],
        body: liquidacionesIVAData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [147, 51, 234] },
        styles: { fontSize: 9 }
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;
    }

    // Liquidaciones Modelo 130
    if (proyeccionCalculada.liquidaciones_modelo130 && proyeccionCalculada.liquidaciones_modelo130.length > 0) {
      // Nueva página si es necesario
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Liquidaciones Modelo 130 (Impuestos Directos)', 14, yPos);
      yPos += 8;

      const liquidaciones130Data = [
        ['Periodo', 'Beneficio Neto', 'Porcentaje', 'Importe', 'Fecha Vencimiento']
      ];

      proyeccionCalculada.liquidaciones_modelo130.forEach(liq => {
        liquidaciones130Data.push([
          liq.periodo,
          `${formatNumberES(liq.beneficio_neto)}€`,
          `${liq.porcentaje}%`,
          `${formatNumberES(liq.importe)}€`,
          new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')
        ]);
      });

      autoTable(doc, {
        startY: yPos,
        head: [liquidaciones130Data[0]],
        body: liquidaciones130Data.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [249, 115, 22] },
        styles: { fontSize: 9 }
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;
    }

    // Verificación Flujo de Caja
    if (proyeccionCalculada.totales.iva_repercutido_total !== undefined) {
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Verificación Flujo de Caja', 14, yPos);
      yPos += 8;

      const beneficioAntesImpuestos = proyeccionCalculada.totales.beneficio_antes_impuestos_total || 
        (proyeccionCalculada.totales.beneficio_neto_total + proyeccionCalculada.totales.impuestos_directos_total);
      const flujoCajaEsperado = beneficioAntesImpuestos + 
        proyeccionCalculada.totales.iva_repercutido_total - 
        (proyeccionCalculada.totales.liquidaciones_pagadas_total || 0) -
        (proyeccionCalculada.totales.cobros_pendientes_clientes || 0) +
        (proyeccionCalculada.totales.pagos_pendientes_proveedores || 0);

      doc.setFontSize(10);
      const verificacionData = [
        ['Concepto', 'Valor'],
        ['Beneficio Antes de Impuestos', `${formatNumberES(beneficioAntesImpuestos)}€`],
        ['+ IVA Repercutido (en cobros)', `+${formatNumberES(proyeccionCalculada.totales.iva_repercutido_total)}€`],
        ['- Liquidaciones Pagadas', `-${formatNumberES(proyeccionCalculada.totales.liquidaciones_pagadas_total || 0)}€`],
        ['- Cobros Pendientes Clientes', `-${formatNumberES(proyeccionCalculada.totales.cobros_pendientes_clientes || 0)}€`],
        ['+ Pagos Pendientes Proveedores', `+${formatNumberES(proyeccionCalculada.totales.pagos_pendientes_proveedores || 0)}€`],
        ['Flujo Caja Esperado', `${formatNumberES(flujoCajaEsperado)}€`],
        ['Flujo Caja Real', `${formatNumberES(proyeccionCalculada.totales.flujo_caja_total)}€`]
      ];

      if (Math.abs(proyeccionCalculada.totales.flujo_caja_total - flujoCajaEsperado) > 0.01) {
        verificacionData.push([
          'Diferencia', 
          `${formatNumberES(Math.abs(proyeccionCalculada.totales.flujo_caja_total - flujoCajaEsperado))}€`
        ]);
      }

      autoTable(doc, {
        startY: yPos,
        head: [verificacionData[0]],
        body: verificacionData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] }
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Nota: El IVA Soportado no deducible ya está incluido en los pagos (se paga con las facturas), por lo que no se resta del flujo de caja.', 14, yPos, { maxWidth: pageWidth - 28 });
      yPos += 10;
      doc.setTextColor(0, 0, 0);
    }

    // Liquidaciones Pendientes de Pago
    if ((proyeccionCalculada.totales.liquidaciones_pendientes_iva?.length > 0 || 
         proyeccionCalculada.totales.liquidaciones_pendientes_130?.length > 0)) {
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Liquidaciones Pendientes de Pago', 14, yPos);
      yPos += 8;

      doc.setFontSize(9);
      doc.text('Las siguientes liquidaciones vencen después del período de la proyección y no están incluidas en el flujo de caja:', 14, yPos);
      yPos += 8;

      const pendientesData = [['Tipo', 'Periodo', 'Importe', 'Fecha Vencimiento']];

      if (proyeccionCalculada.totales.liquidaciones_pendientes_iva) {
        proyeccionCalculada.totales.liquidaciones_pendientes_iva.forEach(liq => {
          pendientesData.push([
            'IVA',
            `T${liq.trimestre} ${liq.año}`,
            `${formatNumberES(liq.importe)}€`,
            new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')
          ]);
        });
      }

      if (proyeccionCalculada.totales.liquidaciones_pendientes_130) {
        proyeccionCalculada.totales.liquidaciones_pendientes_130.forEach(liq => {
          pendientesData.push([
            'Modelo 130',
            liq.periodo,
            `${formatNumberES(liq.importe)}€`,
            new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')
          ]);
        });
      }

      const totalPendiente = (proyeccionCalculada.totales.liquidaciones_pendientes_iva || []).reduce((sum, liq) => sum + liq.importe, 0) +
        (proyeccionCalculada.totales.liquidaciones_pendientes_130 || []).reduce((sum, liq) => sum + liq.importe, 0);
      pendientesData.push(['TOTAL PENDIENTE', '', `${formatNumberES(totalPendiente)}€`, '']);

      autoTable(doc, {
        startY: yPos,
        head: [pendientesData[0]],
        body: pendientesData.slice(1, -1),
        foot: [pendientesData[pendientesData.length - 1]],
        theme: 'striped',
        headStyles: { fillColor: [234, 179, 8] },
        footStyles: { fillColor: [234, 179, 8], fontStyle: 'bold' },
        styles: { fontSize: 9 }
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;
    }

    // Agregar gráficos al PDF
    try {
      // Esperar un momento para asegurar que los gráficos estén renderizados
      await new Promise(resolve => setTimeout(resolve, 500));

      // Gráfico de ingresos y costes - siempre con salto de página antes
      const graficoIngresosElement = document.querySelector('[data-chart="ingresos"]');
      if (graficoIngresosElement) {
        doc.addPage();
        yPos = 20;
        
        doc.setFontSize(14);
        doc.text('Gráfico: Cuenta de Explotación', 14, yPos);
        yPos += 10;

        const canvasIngresos = await html2canvas(graficoIngresosElement, {
          backgroundColor: '#ffffff',
          scale: 1.5,
          logging: false,
          useCORS: true
        });
        
        const imgDataIngresos = canvasIngresos.toDataURL('image/png');
        const imgWidth = pageWidth - 28;
        const imgHeight = (canvasIngresos.height * imgWidth) / canvasIngresos.width;
        
        doc.addImage(imgDataIngresos, 'PNG', 14, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 15;
      }

      // Gráfico de tesorería - continúa en la misma página si hay espacio
      const graficoTesoreriaElement = document.querySelector('[data-chart="tesoreria"]');
      if (graficoTesoreriaElement) {
        doc.setFontSize(14);
        doc.text('Gráfico: Flujo de Caja', 14, yPos);
        yPos += 10;

        const canvasTesoreria = await html2canvas(graficoTesoreriaElement, {
          backgroundColor: '#ffffff',
          scale: 1.5,
          logging: false,
          useCORS: true
        });
        
        const imgDataTesoreria = canvasTesoreria.toDataURL('image/png');
        const imgWidth = pageWidth - 28;
        const imgHeight = (canvasTesoreria.height * imgWidth) / canvasTesoreria.width;
        
        // Solo hacer salto de página si realmente no cabe
        if (yPos + imgHeight > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
          doc.setFontSize(14);
          doc.text('Gráfico: Flujo de Caja', 14, yPos);
          yPos += 10;
        }
        
        doc.addImage(imgDataTesoreria, 'PNG', 14, yPos, imgWidth, imgHeight);
      }
    } catch (error) {
      // Continuar sin gráficos si hay error
    }

    // Guardar PDF
    doc.save(`${proyeccionCalculada.proyeccion.nombre.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Proyección exportada a PDF');
  };

  // Función para exportar a Excel
  const exportarExcel = () => {
    if (!proyeccionCalculada) return;

    const wb = XLSX.utils.book_new();

    // Hoja 1: Resumen
    const resumenData = [
      ['Proyección:', proyeccionCalculada.proyeccion.nombre],
      ['Fecha de inicio:', new Date(proyeccionCalculada.proyeccion.fecha_inicio).toLocaleDateString('es-ES')],
      ['Meses proyectados:', proyeccionCalculada.proyeccion.meses_proyectados],
      [],
      ['RESUMEN DE TOTALES'],
      ['Concepto', 'Total'],
      ['Ingresos Total', proyeccionCalculada.totales.ingresos_total],
      ['Costes Variables', proyeccionCalculada.totales.costes_variables_total],
      ['Gastos Fijos', proyeccionCalculada.totales.gastos_fijos_total],
      ['Beneficio Neto', proyeccionCalculada.totales.beneficio_neto_total],
      ['Flujo de Caja Total', proyeccionCalculada.totales.flujo_caja_total],
      []
    ];

    if (metricas) {
      resumenData.push(
        ['MÉTRICAS RESUMIDAS'],
        ['Concepto', 'Valor'],
        ['Ingresos Promedio Mensual', metricas.ingresosPromedioMensual],
        ['Costes Promedio Mensual', metricas.costesPromedioMensual],
        ['Beneficio Promedio Mensual', metricas.beneficioPromedioMensual],
        ['Flujo Caja Promedio Mensual', metricas.flujoCajaPromedioMensual],
        ['Margen Bruto (%)', metricas.margenBruto],
        ['Meses con Beneficio Positivo', metricas.mesesPositivos],
        ['Meses con Beneficio Negativo', metricas.mesesNegativos],
        ['Saldo Final', metricas.saldoFinal],
        ['Saldo Máximo', metricas.saldoMaximo],
        ['Saldo Mínimo', metricas.saldoMinimo]
      );
    }

    if (metricas && metricas.horasTotal > 0) {
      resumenData.push(
        [],
        ['ANÁLISIS DE HORAS TRABAJADAS'],
        ['Concepto', 'Valor'],
        ['Horas Totales', metricas.horasTotal],
        ['Promedio Mensual', metricas.horasPromedioMensual],
        ['Máximo Mensual', metricas.horasMaximas],
        ['Mínimo Mensual', metricas.horasMinimas],
        ['PVP por Hora (sin IVA)', metricas.precioVentaPorHora],
        ['Beneficio por Hora', metricas.beneficioPorHora]
      );
    }

    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

    // Hoja 2: Detalle Mes a Mes (transpuesta: conceptos como filas)
    const detalleHeader = ['Concepto', ...proyeccionCalculada.meses.map((mes) => {
      return mes.nombre_mes && mes.año_calendario 
        ? `${mes.nombre_mes} ${mes.año_calendario}` 
        : `Mes ${mes.mes}`;
    })];

    const detalleData = [detalleHeader];
    
    // Conceptos como filas
    detalleData.push(['Ingresos', ...proyeccionCalculada.meses.map(m => m.ingresos)]);
    detalleData.push(['Costes Var.', ...proyeccionCalculada.meses.map(m => m.costes_variables)]);
    detalleData.push(['Beneficio Bruto', ...proyeccionCalculada.meses.map(m => m.beneficio_bruto || (m.ingresos - m.costes_variables))]);
    detalleData.push(['Gastos Fijos', ...proyeccionCalculada.meses.map(m => m.gastos_fijos)]);
    detalleData.push(['IVA No Ded.', ...proyeccionCalculada.meses.map(m => m.iva_no_deducible || 0)]);
    detalleData.push(['Benef. Antes Imp.', ...proyeccionCalculada.meses.map(m => m.beneficio_antes_impuestos || m.beneficio_neto)]);
    detalleData.push(['Impuestos', ...proyeccionCalculada.meses.map(m => m.impuestos_directos || 0)]);
    detalleData.push(['Beneficio Neto', ...proyeccionCalculada.meses.map(m => m.beneficio_neto)]);
    detalleData.push(['Cobros', ...proyeccionCalculada.tesoreria.map(t => t.cobros_mes)]);
    detalleData.push(['Pagos', ...proyeccionCalculada.tesoreria.map(t => t.pagos_mes)]);
    detalleData.push(['Flujo Caja', ...proyeccionCalculada.tesoreria.map(t => t.flujo_caja)]);
    detalleData.push(['Saldo Acum.', ...proyeccionCalculada.tesoreria.map(t => t.saldo_acumulado)]);

    const wsDetalle = XLSX.utils.aoa_to_sheet(detalleData);
    XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle Mes a Mes');

    // Hoja 3: Liquidaciones IVA
    if (proyeccionCalculada.liquidaciones_iva && proyeccionCalculada.liquidaciones_iva.length > 0) {
      const liquidacionesIVAData = [
        ['Trimestre', 'IVA Repercutido', 'IVA Soportado', 'IVA a Pagar', 'Fecha Vencimiento']
      ];

      proyeccionCalculada.liquidaciones_iva.forEach(liq => {
        liquidacionesIVAData.push([
          `T${liq.trimestre} ${liq.año}`,
          liq.iva_repercutido,
          liq.iva_soportado,
          liq.iva_a_pagar,
          new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')
        ]);
      });

      const wsIVA = XLSX.utils.aoa_to_sheet(liquidacionesIVAData);
      XLSX.utils.book_append_sheet(wb, wsIVA, 'Liquidaciones IVA');
    }

    // Hoja 4: Liquidaciones Modelo 130
    if (proyeccionCalculada.liquidaciones_modelo130 && proyeccionCalculada.liquidaciones_modelo130.length > 0) {
      const liquidaciones130Data = [
        ['Periodo', 'Beneficio Neto', 'Porcentaje', 'Importe', 'Fecha Vencimiento']
      ];

      proyeccionCalculada.liquidaciones_modelo130.forEach(liq => {
        liquidaciones130Data.push([
          liq.periodo,
          liq.beneficio_neto,
          liq.porcentaje,
          liq.importe,
          new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')
        ]);
      });

      const ws130 = XLSX.utils.aoa_to_sheet(liquidaciones130Data);
      XLSX.utils.book_append_sheet(wb, ws130, 'Liquidaciones 130');
    }

    // Hoja 5: Verificación Flujo de Caja
    if (proyeccionCalculada.totales.iva_repercutido_total !== undefined) {
      const beneficioAntesImpuestos = proyeccionCalculada.totales.beneficio_antes_impuestos_total || 
        (proyeccionCalculada.totales.beneficio_neto_total + proyeccionCalculada.totales.impuestos_directos_total);
      const flujoCajaEsperado = beneficioAntesImpuestos + 
        proyeccionCalculada.totales.iva_repercutido_total - 
        (proyeccionCalculada.totales.liquidaciones_pagadas_total || 0) -
        (proyeccionCalculada.totales.cobros_pendientes_clientes || 0) +
        (proyeccionCalculada.totales.pagos_pendientes_proveedores || 0);

      const verificacionData = [
        ['VERIFICACIÓN FLUJO DE CAJA'],
        ['Concepto', 'Valor'],
        ['Beneficio Antes de Impuestos', beneficioAntesImpuestos],
        ['+ IVA Repercutido (en cobros)', proyeccionCalculada.totales.iva_repercutido_total],
        ['- Liquidaciones Pagadas', proyeccionCalculada.totales.liquidaciones_pagadas_total || 0],
        ['- Cobros Pendientes Clientes', proyeccionCalculada.totales.cobros_pendientes_clientes || 0],
        ['+ Pagos Pendientes Proveedores', proyeccionCalculada.totales.pagos_pendientes_proveedores || 0],
        ['Flujo Caja Esperado', flujoCajaEsperado],
        ['Flujo Caja Real', proyeccionCalculada.totales.flujo_caja_total]
      ];

      if (Math.abs(proyeccionCalculada.totales.flujo_caja_total - flujoCajaEsperado) > 0.01) {
        verificacionData.push(['Diferencia', Math.abs(proyeccionCalculada.totales.flujo_caja_total - flujoCajaEsperado)]);
      }

      verificacionData.push([]);
      verificacionData.push(['Nota: El IVA Soportado no deducible ya está incluido en los pagos (se paga con las facturas), por lo que no se resta del flujo de caja.']);

      const wsVerificacion = XLSX.utils.aoa_to_sheet(verificacionData);
      XLSX.utils.book_append_sheet(wb, wsVerificacion, 'Verificación Flujo');
    }

    // Hoja 6: Liquidaciones Pendientes
    if ((proyeccionCalculada.totales.liquidaciones_pendientes_iva?.length > 0 || 
         proyeccionCalculada.totales.liquidaciones_pendientes_130?.length > 0)) {
      const pendientesData = [
        ['LIQUIDACIONES PENDIENTES DE PAGO'],
        ['Las siguientes liquidaciones vencen después del período de la proyección y no están incluidas en el flujo de caja:'],
        [],
        ['Tipo', 'Periodo', 'Importe', 'Fecha Vencimiento']
      ];

      if (proyeccionCalculada.totales.liquidaciones_pendientes_iva) {
        proyeccionCalculada.totales.liquidaciones_pendientes_iva.forEach(liq => {
          pendientesData.push([
            'IVA',
            `T${liq.trimestre} ${liq.año}`,
            liq.importe,
            new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')
          ]);
        });
      }

      if (proyeccionCalculada.totales.liquidaciones_pendientes_130) {
        proyeccionCalculada.totales.liquidaciones_pendientes_130.forEach(liq => {
          pendientesData.push([
            'Modelo 130',
            liq.periodo,
            liq.importe,
            new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')
          ]);
        });
      }

      const totalPendiente = (proyeccionCalculada.totales.liquidaciones_pendientes_iva || []).reduce((sum, liq) => sum + liq.importe, 0) +
        (proyeccionCalculada.totales.liquidaciones_pendientes_130 || []).reduce((sum, liq) => sum + liq.importe, 0);
      pendientesData.push([]);
      pendientesData.push(['TOTAL PENDIENTE', '', totalPendiente, '']);

      const wsPendientes = XLSX.utils.aoa_to_sheet(pendientesData);
      XLSX.utils.book_append_sheet(wb, wsPendientes, 'Liquidaciones Pend.');
    }

    // Guardar Excel
    XLSX.writeFile(wb, `${proyeccionCalculada.proyeccion.nombre.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Proyección exportada a Excel');
  };

  // Función para obtener los nombres de los meses basados en la fecha de inicio
  const obtenerNombresMesesProyeccion = (fechaInicio, mesesProyectados) => {
    if (!fechaInicio) {
      // Si no hay fecha, usar meses genéricos
      return nombresMeses.slice(0, mesesProyectados);
    }
    
    const fecha = new Date(fechaInicio);
    const meses = [];
    
    for (let i = 0; i < mesesProyectados; i++) {
      const fechaMes = new Date(fecha.getFullYear(), fecha.getMonth() + i, 1);
      // Formato corto: "Nov 2025"
      const nombreMes = fechaMes.toLocaleDateString('es-ES', { month: 'short' });
      const año = fechaMes.getFullYear();
      meses.push(`${nombreMes} ${año}`);
    }
    
    return meses;
  };

  // Función para exportar comparación a PDF
  const exportarComparacionPDF = async () => {
    if (proyeccionesComparadas.length === 0) return;

    toast.info('Generando PDF de comparación...', { duration: 2000 });

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // Título
    doc.setFontSize(18);
    doc.text('Comparación de Proyecciones', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    doc.setFontSize(12);
    const nombresProyecciones = proyeccionesComparadas.map(p => p.proyeccion.nombre).join(' vs ');
    doc.text(nombresProyecciones, pageWidth / 2, yPos, { align: 'center', maxWidth: pageWidth - 40 });
    yPos += 15;

    // Resumen comparativo
    doc.setFontSize(14);
    doc.text('Resumen Comparativo', 14, yPos);
    yPos += 8;

    const resumenHeaders = ['Métrica', ...proyeccionesComparadas.map(p => p.proyeccion.nombre)];
    const resumenRows = [
      ['Ingresos Total', ...proyeccionesComparadas.map(p => `${formatNumberES(p.totales.ingresos_total)}€`)],
      ['Costes Total', ...proyeccionesComparadas.map(p => `${formatNumberES(p.totales.costes_variables_total + p.totales.gastos_fijos_total)}€`)],
      ['Beneficio Neto', ...proyeccionesComparadas.map(p => `${formatNumberES(p.totales.beneficio_neto_total)}€`)],
      ['Flujo Caja Total', ...proyeccionesComparadas.map(p => `${formatNumberES(p.totales.flujo_caja_total)}€`)],
      ['Ingresos Promedio Mensual', ...proyeccionesComparadas.map(p => {
        const promedio = p.totales.ingresos_total / (p.proyeccion.meses_proyectados || 1);
        return `${formatNumberES(promedio)}€`;
      })],
      ['Beneficio Promedio Mensual', ...proyeccionesComparadas.map(p => {
        const promedio = p.totales.beneficio_neto_total / (p.proyeccion.meses_proyectados || 1);
        return `${formatNumberES(promedio)}€`;
      })]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [resumenHeaders],
      body: resumenRows,
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] },
      styles: { fontSize: 9 },
      margin: { left: 14, right: 14 }
    });
    yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;

    // Tabla de unidades por producto
    if (productos.length > 0) {
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Comparación de Unidades por Producto', 14, yPos);
      yPos += 8;

      const unidadesHeaders = ['Producto', ...proyeccionesComparadas.map(p => p.proyeccion.nombre)];
      const unidadesRows = productos.map(producto => {
        const unidadesPorProyeccion = proyeccionesComparadas.map(p => {
          const unidadesPorMes = p.proyeccion?.unidades_por_mes || {};
          const unidades = unidadesPorMes[producto.id] || [];
          const total = Array.isArray(unidades) 
            ? unidades.reduce((sum, u) => sum + (parseFloat(u) || 0), 0)
            : 0;
          return `${formatNumberES(total)} unidades`;
        });
        return [producto.nombre, ...unidadesPorProyeccion];
      });

      autoTable(doc, {
        startY: yPos,
        head: [unidadesHeaders],
        body: unidadesRows,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 8 },
        margin: { left: 14, right: 14 }
      });
      yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;
    }

    // Tabla comparativa de costes
    if (yPos > pageHeight - 100) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.text('Comparación de Costes', 14, yPos);
    yPos += 8;

    const costesHeaders = ['Tipo de Coste', ...proyeccionesComparadas.map(p => p.proyeccion.nombre)];
    const costesRows = [
      ['Costes Variables Total', ...proyeccionesComparadas.map(p => `${formatNumberES(p.totales.costes_variables_total)}€`)],
      ['Gastos Fijos Total', ...proyeccionesComparadas.map(p => `${formatNumberES(p.totales.gastos_fijos_total)}€`)],
      ['Total Costes', ...proyeccionesComparadas.map(p => `${formatNumberES(p.totales.costes_variables_total + p.totales.gastos_fijos_total)}€`)],
      ['Coste Promedio Mensual', ...proyeccionesComparadas.map(p => {
        const promedio = (p.totales.costes_variables_total + p.totales.gastos_fijos_total) / (p.proyeccion.meses_proyectados || 1);
        return `${formatNumberES(promedio)}€`;
      })]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [costesHeaders],
      body: costesRows,
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94] },
      styles: { fontSize: 9 },
      margin: { left: 14, right: 14 }
    });
    yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : yPos + 30;

    // Agregar gráficos comparativos
    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      // Gráfico de beneficios
      const graficoBeneficiosElement = document.querySelector('[data-chart="comparacion-beneficios"]');
      if (graficoBeneficiosElement) {
        doc.addPage();
        yPos = 20;
        
        doc.setFontSize(14);
        doc.text('Gráfico: Comparación de Beneficios Netos', 14, yPos);
        yPos += 10;

        const canvasBeneficios = await html2canvas(graficoBeneficiosElement, {
          backgroundColor: '#ffffff',
          scale: 1.5,
          logging: false,
          useCORS: true
        });
        
        const imgDataBeneficios = canvasBeneficios.toDataURL('image/png');
        const imgWidth = pageWidth - 28;
        const imgHeight = (canvasBeneficios.height * imgWidth) / canvasBeneficios.width;
        
        doc.addImage(imgDataBeneficios, 'PNG', 14, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 15;
      }

      // Gráfico de tesorería
      const graficoTesoreriaElement = document.querySelector('[data-chart="comparacion-tesoreria"]');
      if (graficoTesoreriaElement) {
        if (yPos + 200 > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(14);
        doc.text('Gráfico: Comparación de Tesorería Acumulada', 14, yPos);
        yPos += 10;

        const canvasTesoreria = await html2canvas(graficoTesoreriaElement, {
          backgroundColor: '#ffffff',
          scale: 1.5,
          logging: false,
          useCORS: true
        });
        
        const imgDataTesoreria = canvasTesoreria.toDataURL('image/png');
        const imgWidth = pageWidth - 28;
        const imgHeight = (canvasTesoreria.height * imgWidth) / canvasTesoreria.width;
        
        doc.addImage(imgDataTesoreria, 'PNG', 14, yPos, imgWidth, imgHeight);
      }
    } catch (error) {
      // Continuar sin gráficos si hay error
    }

    // Guardar PDF
    const nombreArchivo = `Comparacion_${proyeccionesComparadas.map(p => p.proyeccion.nombre.replace(/[^a-z0-9]/gi, '_')).join('_vs_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(nombreArchivo);
    toast.success('Comparación exportada a PDF');
  };

  // Función para exportar comparación a Excel
  const exportarComparacionExcel = () => {
    if (proyeccionesComparadas.length === 0) return;

    const wb = XLSX.utils.book_new();

    // Hoja 1: Resumen Comparativo
    const resumenData = [
      ['COMPARACIÓN DE PROYECCIONES'],
      [],
      ['Proyecciones:', ...proyeccionesComparadas.map(p => p.proyeccion.nombre)],
      [],
      ['RESUMEN COMPARATIVO'],
      ['Métrica', ...proyeccionesComparadas.map(p => p.proyeccion.nombre)],
      ['Ingresos Total', ...proyeccionesComparadas.map(p => p.totales.ingresos_total)],
      ['Costes Total', ...proyeccionesComparadas.map(p => p.totales.costes_variables_total + p.totales.gastos_fijos_total)],
      ['Beneficio Neto', ...proyeccionesComparadas.map(p => p.totales.beneficio_neto_total)],
      ['Flujo Caja Total', ...proyeccionesComparadas.map(p => p.totales.flujo_caja_total)],
      ['Ingresos Promedio Mensual', ...proyeccionesComparadas.map(p => p.totales.ingresos_total / (p.proyeccion.meses_proyectados || 1))],
      ['Beneficio Promedio Mensual', ...proyeccionesComparadas.map(p => p.totales.beneficio_neto_total / (p.proyeccion.meses_proyectados || 1))]
    ];

    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

    // Hoja 2: Unidades por Producto
    if (productos.length > 0) {
      const unidadesData = [
        ['Producto', ...proyeccionesComparadas.map(p => p.proyeccion.nombre)]
      ];

      productos.forEach(producto => {
        const unidadesPorProyeccion = proyeccionesComparadas.map(p => {
          const unidadesPorMes = p.proyeccion?.unidades_por_mes || {};
          const unidades = unidadesPorMes[producto.id] || [];
          const total = Array.isArray(unidades) 
            ? unidades.reduce((sum, u) => sum + (parseFloat(u) || 0), 0)
            : 0;
          return total;
        });
        unidadesData.push([producto.nombre, ...unidadesPorProyeccion]);
      });

      const wsUnidades = XLSX.utils.aoa_to_sheet(unidadesData);
      XLSX.utils.book_append_sheet(wb, wsUnidades, 'Unidades');
    }

    // Hoja 3: Costes Comparativos
    const costesData = [
      ['Tipo de Coste', ...proyeccionesComparadas.map(p => p.proyeccion.nombre)],
      ['Costes Variables Total', ...proyeccionesComparadas.map(p => p.totales.costes_variables_total)],
      ['Gastos Fijos Total', ...proyeccionesComparadas.map(p => p.totales.gastos_fijos_total)],
      ['Total Costes', ...proyeccionesComparadas.map(p => p.totales.costes_variables_total + p.totales.gastos_fijos_total)],
      ['Coste Promedio Mensual', ...proyeccionesComparadas.map(p => {
        return (p.totales.costes_variables_total + p.totales.gastos_fijos_total) / (p.proyeccion.meses_proyectados || 1);
      })]
    ];

    const wsCostes = XLSX.utils.aoa_to_sheet(costesData);
    XLSX.utils.book_append_sheet(wb, wsCostes, 'Costes');

    // Hoja 4: Beneficios Mes a Mes (para cada proyección)
    proyeccionesComparadas.forEach((p, idx) => {
      const beneficiosData = [
        [`${p.proyeccion.nombre} - Beneficios Mes a Mes`],
        ['Mes', 'Beneficio Neto']
      ];

      p.meses.forEach(mes => {
        const mesLabel = mes.nombre_mes && mes.año_calendario 
          ? `${mes.nombre_mes} ${mes.año_calendario}` 
          : `Mes ${mes.mes}`;
        beneficiosData.push([mesLabel, mes.beneficio_neto]);
      });

      const wsBeneficios = XLSX.utils.aoa_to_sheet(beneficiosData);
      XLSX.utils.book_append_sheet(wb, wsBeneficios, `Beneficios ${idx + 1}`);
    });

    // Hoja 5: Tesorería Mes a Mes (para cada proyección)
    proyeccionesComparadas.forEach((p, idx) => {
      const tesoreriaData = [
        [`${p.proyeccion.nombre} - Tesorería Mes a Mes`],
        ['Mes', 'Saldo Acumulado']
      ];

      p.tesoreria.forEach(t => {
        const mesLabel = t.nombre_mes && t.año_calendario 
          ? `${t.nombre_mes} ${t.año_calendario}` 
          : `Mes ${t.mes}`;
        tesoreriaData.push([mesLabel, t.saldo_acumulado]);
      });

      const wsTesoreria = XLSX.utils.aoa_to_sheet(tesoreriaData);
      XLSX.utils.book_append_sheet(wb, wsTesoreria, `Tesorería ${idx + 1}`);
    });

    // Guardar Excel
    const nombreArchivo = `Comparacion_${proyeccionesComparadas.map(p => p.proyeccion.nombre.replace(/[^a-z0-9]/gi, '_')).join('_vs_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);
    toast.success('Comparación exportada a Excel');
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="page-title" data-testid="proyecciones-title">Proyecciones</h1>
            <p className="page-subtitle">Proyecta tus ingresos y costes mes a mes con tesorería</p>
          </div>
          <div className="flex gap-2">
            {proyecciones.length >= 2 && (
              <Button 
                variant="outline" 
                onClick={() => setCompararModalOpen(true)}
                data-testid="btn-comparar-proyecciones"
              >
                <GitCompare size={18} className="mr-2" /> Comparar
              </Button>
            )}
            <Button onClick={() => abrirModalProyeccion(null)} data-testid="btn-nueva-proyeccion">
              <Plus size={18} className="mr-2" /> Nueva Proyección
            </Button>
          </div>
        </div>
      </div>

      <div className="page-body">
        {proyecciones.length === 0 ? (
          <Card className="card text-center py-12" data-testid="proyecciones-empty">
            <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No hay proyecciones creadas</h3>
            <p className="text-gray-600 mb-4">Crea tu primera proyección para ver ingresos y costes mes a mes</p>
            <Button onClick={() => abrirModalProyeccion(null)} data-testid="btn-crear-primera">
              <Plus size={18} className="mr-2" /> Crear Primera Proyección
            </Button>
          </Card>
        ) : (
          <>
            {(() => {
              // Filtrar proyecciones
              const proyeccionesFiltradas = proyecciones.filter(p => {
                // Filtro por texto
                if (filtroTexto && !p.nombre.toLowerCase().includes(filtroTexto.toLowerCase()) && 
                    !(p.descripcion && p.descripcion.toLowerCase().includes(filtroTexto.toLowerCase()))) {
                  return false;
                }
                
                // Filtro por fecha desde
                if (filtroFechaDesde) {
                  const fechaInicio = new Date(p.fecha_inicio);
                  const fechaDesde = new Date(filtroFechaDesde);
                  if (fechaInicio < fechaDesde) return false;
                }
                
                // Filtro por fecha hasta
                if (filtroFechaHasta) {
                  const fechaInicio = new Date(p.fecha_inicio);
                  const fechaHasta = new Date(filtroFechaHasta);
                  if (fechaInicio > fechaHasta) return false;
                }
                
                // Filtro por meses
                if (filtroMeses) {
                  if (p.meses_proyectados !== parseInt(filtroMeses)) return false;
                }
                
                return true;
              });

              return (
                <>
                  {/* Filtros */}
                  <Card className="p-4 mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                          placeholder="Buscar por nombre..."
                          value={filtroTexto}
                          onChange={(e) => setFiltroTexto(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      <Input
                        type="date"
                        placeholder="Fecha desde"
                        value={filtroFechaDesde}
                        onChange={(e) => setFiltroFechaDesde(e.target.value)}
                        className="text-sm"
                      />
                      <Input
                        type="date"
                        placeholder="Fecha hasta"
                        value={filtroFechaHasta}
                        onChange={(e) => setFiltroFechaHasta(e.target.value)}
                        className="text-sm"
                      />
                      <Select value={filtroMeses || undefined} onValueChange={(value) => setFiltroMeses(value || '')}>
                        <SelectTrigger>
                          <SelectValue placeholder="Todos los meses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 meses</SelectItem>
                          <SelectItem value="6">6 meses</SelectItem>
                          <SelectItem value="12">12 meses</SelectItem>
                          <SelectItem value="18">18 meses</SelectItem>
                          <SelectItem value="24">24 meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(filtroTexto || filtroFechaDesde || filtroFechaHasta || filtroMeses) && (
                      <div className="mt-3 flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFiltroTexto('');
                            setFiltroFechaDesde('');
                            setFiltroFechaHasta('');
                            setFiltroMeses('');
                          }}
                          className="flex items-center gap-1"
                        >
                          <X size={14} />
                          Limpiar filtros
                        </Button>
                        <span className="text-sm text-gray-500">
                          {proyeccionesFiltradas.length} de {proyecciones.length} proyecciones
                        </span>
                      </div>
                    )}
                  </Card>

                  <div className="grid gap-4">
                    {proyeccionesFiltradas.length === 0 ? (
                      <Card className="card text-center py-8">
                        <p className="text-gray-500">No se encontraron proyecciones con los filtros aplicados</p>
                      </Card>
                    ) : (
                      proyeccionesFiltradas.map((proyeccion) => {
                  // Calcular productos con unidades definidas y total de unidades
                  const unidadesPorMes = proyeccion.unidades_por_mes || {};
                  const productosConUnidades = Object.keys(unidadesPorMes).filter(productoId => {
                    const unidades = unidadesPorMes[productoId] || [];
                    return Array.isArray(unidades) && unidades.some(u => parseFloat(u) > 0);
                  });
                  const totalUnidades = Math.round(Object.values(unidadesPorMes).reduce((total, unidades) => {
                    if (Array.isArray(unidades)) {
                      return total + unidades.reduce((sum, u) => sum + (parseFloat(u) || 0), 0);
                    }
                    return total;
                  }, 0));

                  // Calcular horas medias mensuales trabajadas
                  let horasMediasMensuales = 0;
                  if (productos.length > 0 && productosConUnidades.length > 0) {
                    let horasTotales = 0;
                    productosConUnidades.forEach(productoId => {
                      const producto = productos.find(p => p.id === productoId);
                      if (producto) {
                        const unidades = unidadesPorMes[productoId] || [];
                        const totalUnidadesProducto = Array.isArray(unidades) 
                          ? unidades.reduce((sum, u) => sum + (parseFloat(u) || 0), 0)
                          : 0;
                        
                        const duracionHoras = (producto.duracion_minutos || 60) / 60.0;
                        const tiempoPrepHoras = (producto.tiempo_preparacion_minutos || 0) / 60.0;
                        const tipoPrep = producto.tipo_preparacion || 'por_imparticion';
                        
                        let horasPorUnidad = duracionHoras;
                        if (tipoPrep === 'unico') {
                          // Preparación única: se suma una vez al total
                          horasPorUnidad = duracionHoras;
                          horasTotales += totalUnidadesProducto * horasPorUnidad;
                          if (totalUnidadesProducto > 0) {
                            horasTotales += tiempoPrepHoras; // Sumar preparación una vez
                          }
                        } else {
                          // Preparación por impartición: se suma a cada unidad
                          horasPorUnidad = duracionHoras + tiempoPrepHoras;
                          horasTotales += totalUnidadesProducto * horasPorUnidad;
                        }
                      }
                    });
                    horasMediasMensuales = horasTotales / (proyeccion.meses_proyectados || 1);
                  }

                  // Beneficio neto medio mensual (desde cache si está disponible)
                  let beneficioNetoMedioMensual = null;
                  const totales = totalesProyecciones[proyeccion.id];
                  if (totales && totales.beneficio_neto_total !== undefined) {
                    beneficioNetoMedioMensual = totales.beneficio_neto_total / (proyeccion.meses_proyectados || 1);
                  }

                  return (
              <Card key={proyeccion.id} className="card" data-testid={`proyeccion-card-${proyeccion.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3 flex-1">
                    {compararModalOpen && (
                      <button
                        onClick={() => toggleSeleccionProyeccion(proyeccion.id)}
                        className="mt-1 p-1 hover:bg-gray-100 rounded transition-colors"
                        title={proyeccionesSeleccionadas.includes(proyeccion.id) ? 'Deseleccionar' : 'Seleccionar para comparar'}
                      >
                        {proyeccionesSeleccionadas.includes(proyeccion.id) ? (
                          <CheckSquare size={20} className="text-blue-600" />
                        ) : (
                          <Square size={20} className="text-gray-400" />
                        )}
                      </button>
                    )}
                    <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-xl font-bold">{proyeccion.nombre}</h3>
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        {proyeccion.meses_proyectados} meses
                      </Badge>
                      {productosConUnidades.length > 0 ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {productosConUnidades.length} {productosConUnidades.length === 1 ? 'producto' : 'productos'}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">
                          Sin productos
                        </Badge>
                      )}
                      {totalUnidades > 0 && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          {totalUnidades} unidades
                        </Badge>
                      )}
                      {horasMediasMensuales > 0 && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          {formatNumberES(horasMediasMensuales)} h/mes
                        </Badge>
                      )}
                      {beneficioNetoMedioMensual !== null && (
                        <Badge variant="outline" className={`${beneficioNetoMedioMensual >= 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {formatNumberES(beneficioNetoMedioMensual)}€/mes
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {new Date(proyeccion.fecha_inicio).toLocaleDateString('es-ES', { 
                          year: 'numeric', 
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Badge>
                    </div>
                    {proyeccion.descripcion && (
                      <p className="text-sm text-gray-500 mb-3">{proyeccion.descripcion}</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-500">Fecha Inicio</p>
                        <p className="font-semibold">
                          {new Date(proyeccion.fecha_inicio).toLocaleDateString('es-ES', { 
                            year: 'numeric', 
                            month: 'short' 
                          })}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Meses</p>
                        <p className="font-semibold">{proyeccion.meses_proyectados}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Creada</p>
                        <p className="font-semibold">
                          {new Date(proyeccion.created_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Actualizada</p>
                        <p className="font-semibold">
                          {new Date(proyeccion.updated_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                    </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => calcularProyeccion(proyeccion.id)}
                      disabled={loading}
                      data-testid={`btn-calcular-${proyeccion.id}`}
                    >
                      <Eye size={16} className="mr-1" /> Ver
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => abrirModalProyeccion(proyeccion)}
                      data-testid={`btn-edit-${proyeccion.id}`}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      onClick={() => eliminarProyeccion(proyeccion.id)}
                      data-testid={`btn-delete-${proyeccion.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </Card>
                      );
                      })
                    )}
                  </div>
                </>
              );
            })()}
          </>
        )}
      </div>

      {/* Modal para crear/editar proyección */}
      <Dialog open={proyeccionModalOpen} onOpenChange={setProyeccionModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editandoProyeccion ? 'Editar Proyección' : 'Nueva Proyección'}</DialogTitle>
            <DialogDescription>
              {editandoProyeccion ? 'Modifica los datos de la proyección' : 'Crea una nueva proyección financiera'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="proyeccion-nombre">Nombre de la Proyección</Label>
                <Input
                  id="proyeccion-nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Proyección 2026"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="proyeccion-fecha">Fecha de Inicio</Label>
                <Input
                  id="proyeccion-fecha"
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="proyeccion-descripcion">Descripción</Label>
              <Input
                id="proyeccion-descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción opcional de la proyección"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="proyeccion-meses">Meses a Proyectar</Label>
              <Select 
                value={formData.meses_proyectados.toString()} 
                onValueChange={(val) => {
                  const meses = parseInt(val);
                  setFormData({ ...formData, meses_proyectados: meses });
                }}
              >
                <SelectTrigger id="proyeccion-meses">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[3, 6, 12, 18, 24].map(m => (
                    <SelectItem key={m} value={m.toString()}>{m} meses</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tabla de unidades por mes y producto */}
            {productos.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Unidades por Mes y Producto</Label>
                <div className="border rounded-lg overflow-x-auto -mx-6 px-6">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left border-b sticky left-0 bg-gray-50 z-10 min-w-[150px]">Producto</th>
                        {obtenerNombresMesesProyeccion(formData.fecha_inicio, formData.meses_proyectados).map((nombreMes, i) => (
                          <th key={i} className="px-1.5 py-2 text-center border-b min-w-[65px] text-xs">
                            {nombreMes}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {productos.map((producto) => (
                        <tr key={producto.id} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium sticky left-0 bg-white z-10 min-w-[150px]">{producto.nombre}</td>
                          {Array.from({ length: formData.meses_proyectados }, (_, i) => {
                            const unidades = formData.unidades_por_mes[producto.id] || [];
                            return (
                              <td key={i} className="px-1.5 py-1.5">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  className="w-full text-center text-sm font-semibold px-1 py-1 h-8 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  style={{ 
                                    MozAppearance: 'textfield'
                                  }}
                                  value={unidades[i] || 0}
                                  onChange={(e) => actualizarUnidadesMes(producto.id, i, e.target.value)}
                                  placeholder="0"
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500">
                  Introduce las unidades de cada producto que proyectas vender en cada mes
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={cerrarModalProyeccion}>
              Cancelar
            </Button>
            <Button type="button" onClick={guardarProyeccion} data-testid="btn-guardar-proyeccion">
              {editandoProyeccion ? 'Actualizar' : 'Crear'} Proyección
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal para ver proyección calculada */}
      <Dialog open={verProyeccionModalOpen} onOpenChange={setVerProyeccionModalOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle>
                {proyeccionCalculada?.proyeccion.nombre || 'Proyección'}
              </DialogTitle>
              <DialogDescription>
                Detalle completo de la proyección financiera con ingresos, costes y tesorería
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportarPDF}
                className="flex items-center gap-2"
              >
                <FileText size={16} />
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportarExcel}
                className="flex items-center gap-2"
              >
                <FileSpreadsheet size={16} />
                Excel
              </Button>
            </div>
          </DialogHeader>
          
          {proyeccionCalculada && (
            <div className="space-y-6 py-4">
              {/* Resumen de totales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="text-sm text-gray-500">Ingresos Total</div>
                  <div className="text-2xl font-bold text-green-600">
                    {formatNumberES(proyeccionCalculada.totales.ingresos_total)}€
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-gray-500">Costes Total</div>
                  <div className="text-2xl font-bold text-red-600">
                    {formatNumberES(proyeccionCalculada.totales.costes_variables_total + proyeccionCalculada.totales.gastos_fijos_total)}€
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-gray-500">Beneficio Neto</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {formatNumberES(proyeccionCalculada.totales.beneficio_neto_total)}€
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="text-sm text-gray-500">Flujo Caja Total</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {formatNumberES(proyeccionCalculada.totales.flujo_caja_total)}€
                  </div>
                </Card>
              </div>

              {/* Resumen de métricas */}
              {metricas && (
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp size={20} className="text-blue-600" />
                    Resumen de Métricas
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Ingresos Promedio Mensual</div>
                      <div className="text-lg font-semibold text-green-600">
                        {formatNumberES(metricas.ingresosPromedioMensual)}€
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Costes Promedio Mensual</div>
                      <div className="text-lg font-semibold text-red-600">
                        {formatNumberES(metricas.costesPromedioMensual)}€
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Beneficio Promedio Mensual</div>
                      <div className="text-lg font-semibold text-purple-600">
                        {formatNumberES(metricas.beneficioPromedioMensual)}€
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Flujo Caja Promedio Mensual</div>
                      <div className="text-lg font-semibold text-blue-600">
                        {formatNumberES(metricas.flujoCajaPromedioMensual)}€
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Margen Bruto</div>
                      <div className="text-lg font-semibold">
                        {metricas.margenBruto.toFixed(2)}%
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Meses con Beneficio Positivo</div>
                      <div className="text-lg font-semibold text-green-600">
                        {metricas.mesesPositivos} / {proyeccionCalculada.meses.length}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Saldo Final</div>
                      <div className={`text-lg font-semibold ${metricas.saldoFinal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatNumberES(metricas.saldoFinal)}€
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Saldo Máximo</div>
                      <div className="text-lg font-semibold text-green-600">
                        {formatNumberES(metricas.saldoMaximo)}€
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Análisis de Horas Trabajadas */}
              {metricas && metricas.horasTotal > 0 && (
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock size={20} className="text-orange-600" />
                    Análisis de Horas Trabajadas
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Horas Totales</div>
                      <div className="text-lg font-semibold text-orange-600">
                        {formatNumberES(metricas.horasTotal, 1)}h
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Promedio Mensual</div>
                      <div className="text-lg font-semibold text-orange-600">
                        {formatNumberES(metricas.horasPromedioMensual, 1)}h
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Máximo Mensual</div>
                      <div className="text-lg font-semibold text-orange-600">
                        {formatNumberES(metricas.horasMaximas, 1)}h
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Mínimo Mensual</div>
                      <div className="text-lg font-semibold text-orange-600">
                        {formatNumberES(metricas.horasMinimas, 1)}h
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">PVP por Hora (sin IVA)</div>
                      <div className="text-lg font-semibold text-green-600">
                        {formatNumberES(metricas.precioVentaPorHora)}€/h
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">Beneficio por Hora</div>
                      <div className="text-lg font-semibold text-purple-600">
                        {formatNumberES(metricas.beneficioPorHora)}€/h
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Gráfico de ingresos y costes */}
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
                      <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full"></div>
                      <span>Cuenta de Explotación</span>
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 ml-4">Evolución mensual de ingresos, costes y beneficio neto</p>
                </div>
                <div data-chart="ingresos">
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart data={datosGraficoIngresos} margin={{ top: 5, right: 40, left: 0, bottom: 60 }}>
                    <defs>
                      <linearGradient id="gradientIngresos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.02}/>
                      </linearGradient>
                      <linearGradient id="gradientCostes" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02}/>
                      </linearGradient>
                      <linearGradient id="gradientBeneficio" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#667eea" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#667eea" stopOpacity={0.02}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="2 2" />
                    <XAxis 
                      dataKey="mes" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                      interval={0}
                      stroke="#cbd5e1"
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`}
                      stroke="#cbd5e1"
                      tickLine={false}
                      width={70}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const totalIngresos = payload.find(p => p.dataKey === 'ingresos')?.value || 0;
                          const totalCostes = payload.find(p => p.dataKey === 'costes')?.value || 0;
                          const beneficio = payload.find(p => p.dataKey === 'beneficio')?.value || 0;
                          const margen = totalIngresos > 0 ? ((beneficio / totalIngresos) * 100).toFixed(1) : 0;
                          
                          return (
                            <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-xl">
                              <div className="mb-4 pb-3 border-b border-gray-100">
                                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{label}</p>
                              </div>
                              <div className="space-y-3 min-w-[220px]">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    <span className="text-sm font-medium text-gray-700">Ingresos</span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900">{formatNumberES(totalIngresos)}€</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <span className="text-sm font-medium text-gray-700">Costes</span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900">{formatNumberES(totalCostes)}€</span>
                                </div>
                                <div className="pt-2 mt-2 border-t border-gray-100">
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2.5">
                                      <div className="w-3 h-3 rounded-full bg-purple-600"></div>
                                      <span className="text-sm font-semibold text-gray-900">Beneficio Neto</span>
                                    </div>
                                    <span className={`text-base font-bold ${beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {formatNumberES(beneficio)}€
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    Margen: {margen}%
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                      cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="ingresos" 
                      fill="url(#gradientIngresos)" 
                      stroke="none"
                      hide={true}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="costes" 
                      fill="url(#gradientCostes)" 
                      stroke="none"
                      hide={true}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="beneficio" 
                      fill="url(#gradientBeneficio)" 
                      stroke="none"
                      hide={true}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ingresos" 
                      stroke="#10b981" 
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2, fill: '#ffffff' }}
                      name="Ingresos"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="costes" 
                      stroke="#ef4444" 
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2, fill: '#ffffff' }}
                      name="Costes"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="beneficio" 
                      stroke="#667eea" 
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 7, stroke: '#667eea', strokeWidth: 2, fill: '#ffffff' }}
                      name="Beneficio Neto"
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '40px' }}
                      iconType="line"
                      iconSize={14}
                      align="right"
                      verticalAlign="top"
                      formatter={(value) => <span style={{ color: '#475569', fontSize: '12px', fontWeight: 500, letterSpacing: '0.025em' }}>{value}</span>}
                    />
                  </LineChart>
                </ResponsiveContainer>
                </div>
              </Card>

              {/* Gráfico de tesorería */}
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
                      <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
                      <span>Flujo de Caja</span>
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 ml-4">Evolución de cobros, pagos y saldo acumulado</p>
                </div>
                <div data-chart="tesoreria">
                <ResponsiveContainer width="100%" height={500}>
                  <ComposedChart data={datosGraficoTesoreria} margin={{ top: 5, right: 40, left: 0, bottom: 60 }}>
                    <defs>
                      <linearGradient id="gradientCobros" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                      </linearGradient>
                      <linearGradient id="gradientPagos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
                      </linearGradient>
                      <linearGradient id="gradientSaldo" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#667eea" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#667eea" stopOpacity={0.02}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="2 2" />
                    <XAxis 
                      dataKey="mes" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                      interval={0}
                      stroke="#cbd5e1"
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`}
                      stroke="#cbd5e1"
                      tickLine={false}
                      width={70}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const cobros = payload.find(p => p.dataKey === 'cobros')?.value || 0;
                          const pagos = payload.find(p => p.dataKey === 'pagos')?.value || 0;
                          const saldo = payload.find(p => p.dataKey === 'saldo')?.value || 0;
                          const flujo = cobros - pagos;
                          
                          return (
                            <div className="bg-white p-6 border border-gray-200 rounded-lg shadow-xl">
                              <div className="mb-4 pb-3 border-b border-gray-100">
                                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{label}</p>
                              </div>
                              <div className="space-y-3 min-w-[220px]">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    <span className="text-sm font-medium text-gray-700">Cobros</span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900">{formatNumberES(cobros)}€</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <span className="text-sm font-medium text-gray-700">Pagos</span>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900">{formatNumberES(pagos)}€</span>
                                </div>
                                <div className="pt-2 mt-2 border-t border-gray-100">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-700">Flujo Neto</span>
                                    <span className={`text-sm font-semibold ${flujo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {formatNumberES(flujo)}€
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                      <div className="w-3 h-3 rounded-full bg-purple-600"></div>
                                      <span className="text-sm font-semibold text-gray-900">Tesorería Acum.</span>
                                    </div>
                                    <span className={`text-base font-bold ${saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {formatNumberES(saldo)}€
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                      cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '40px' }}
                      iconType="rect"
                      iconSize={12}
                      align="right"
                      verticalAlign="top"
                      formatter={(value) => <span style={{ color: '#475569', fontSize: '12px', fontWeight: 500, letterSpacing: '0.025em' }}>{value}</span>}
                    />
                    <Bar 
                      dataKey="cobros" 
                      fill="#10b981" 
                      name="Cobros"
                      radius={[2, 2, 0, 0]}
                      opacity={0.8}
                    />
                    <Bar 
                      dataKey="pagos" 
                      fill="#ef4444" 
                      name="Pagos"
                      radius={[2, 2, 0, 0]}
                      opacity={0.8}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="saldo" 
                      fill="url(#gradientSaldo)" 
                      stroke="none"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="saldo" 
                      stroke="#667eea" 
                      strokeWidth={3}
                      dot={false}
                      activeDot={{ r: 7, stroke: '#667eea', strokeWidth: 2, fill: '#ffffff' }}
                      name="Tesorería Acum."
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                </div>
              </Card>

              {/* Tabla detallada mes a mes */}
              <Card className="p-4">
                <h3 className="text-lg font-bold mb-4">Detalle Mes a Mes</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left border-b sticky left-0 bg-gray-50 z-10 min-w-[100px]">Concepto</th>
                        {proyeccionCalculada.meses.map((mes, idx) => {
                          const mesLabel = mes.nombre_mes && mes.año_calendario 
                            ? `${mes.nombre_mes} ${mes.año_calendario}` 
                            : `Mes ${mes.mes}`;
                          return (
                            <th key={idx} className="px-2 py-1.5 text-right border-b min-w-[80px]">{mesLabel}</th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium sticky left-0 bg-white z-10">Ingresos</td>
                        {proyeccionCalculada.meses.map((mes, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right text-green-600">{formatNumberES(mes.ingresos)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium sticky left-0 bg-white z-10">Costes Var.</td>
                        {proyeccionCalculada.meses.map((mes, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right text-red-600">-{formatNumberES(mes.costes_variables)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50 bg-gray-50">
                        <td className="px-2 py-1.5 font-semibold sticky left-0 bg-gray-50 z-10">Beneficio Bruto</td>
                        {proyeccionCalculada.meses.map((mes, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right font-semibold">{formatNumberES(mes.beneficio_bruto || (mes.ingresos - mes.costes_variables))}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium sticky left-0 bg-white z-10">Gastos Fijos</td>
                        {proyeccionCalculada.meses.map((mes, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right text-red-600">-{formatNumberES(mes.gastos_fijos)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium sticky left-0 bg-white z-10 text-orange-600">IVA No Ded.</td>
                        {proyeccionCalculada.meses.map((mes, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right text-orange-600">-{formatNumberES(mes.iva_no_deducible || 0)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50 bg-blue-50">
                        <td className="px-2 py-1.5 font-semibold sticky left-0 bg-blue-50 z-10">Benef. Antes Imp.</td>
                        {proyeccionCalculada.meses.map((mes, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right font-semibold">{formatNumberES(mes.beneficio_antes_impuestos || mes.beneficio_neto)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium sticky left-0 bg-white z-10 text-red-600">Impuestos</td>
                        {proyeccionCalculada.meses.map((mes, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right text-red-600">-{formatNumberES(mes.impuestos_directos || 0)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50 bg-green-50">
                        <td className="px-2 py-1.5 font-bold sticky left-0 bg-green-50 z-10 text-green-700">Beneficio Neto</td>
                        {proyeccionCalculada.meses.map((mes, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right font-bold text-green-600">{formatNumberES(mes.beneficio_neto)}€</td>
                        ))}
                      </tr>
                      <tr className="border-t-2 border-gray-300">
                        <td className="px-2 py-1.5 font-medium sticky left-0 bg-white z-10 text-green-600">Cobros</td>
                        {proyeccionCalculada.tesoreria.map((tesoreria, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right text-green-600">{formatNumberES(tesoreria.cobros_mes)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium sticky left-0 bg-white z-10 text-red-600">Pagos</td>
                        {proyeccionCalculada.tesoreria.map((tesoreria, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right text-red-600">-{formatNumberES(tesoreria.pagos_mes)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium sticky left-0 bg-white z-10">Flujo Caja</td>
                        {proyeccionCalculada.tesoreria.map((tesoreria, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right">{formatNumberES(tesoreria.flujo_caja)}€</td>
                        ))}
                      </tr>
                      <tr className="border-b-2 border-gray-300 hover:bg-gray-50 bg-purple-50">
                        <td className="px-2 py-1.5 font-bold sticky left-0 bg-purple-50 z-10 text-purple-700">Saldo Acum.</td>
                        {proyeccionCalculada.tesoreria.map((tesoreria, idx) => (
                          <td key={idx} className="px-2 py-1.5 text-right font-bold text-purple-600">{formatNumberES(tesoreria.saldo_acumulado)}€</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Liquidaciones de IVA */}
              {proyeccionCalculada.liquidaciones_iva && proyeccionCalculada.liquidaciones_iva.length > 0 && (
                <Card className="p-4">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-purple-600" />
                    Liquidaciones Trimestrales de IVA
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left border-b">Trimestre</th>
                          <th className="px-4 py-2 text-right border-b">IVA Repercutido</th>
                          <th className="px-4 py-2 text-right border-b">IVA Soportado</th>
                          <th className="px-4 py-2 text-right border-b">IVA a Pagar</th>
                          <th className="px-4 py-2 text-left border-b">Vencimiento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proyeccionCalculada.liquidaciones_iva.map((liq, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">T{liq.trimestre} {liq.año}</td>
                            <td className="px-4 py-2 text-right">{formatNumberES(liq.iva_repercutido)}€</td>
                            <td className="px-4 py-2 text-right">{formatNumberES(liq.iva_soportado)}€</td>
                            <td className="px-4 py-2 text-right font-semibold text-purple-600">{formatNumberES(liq.iva_a_pagar)}€</td>
                            <td className="px-4 py-2">{new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Liquidaciones Modelo 130 */}
              {proyeccionCalculada.liquidaciones_modelo130 && proyeccionCalculada.liquidaciones_modelo130.length > 0 && (
                <Card className="p-4">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-orange-600" />
                    Liquidaciones Modelo 130 (Impuestos Directos)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left border-b">Periodo</th>
                          <th className="px-4 py-2 text-right border-b">Beneficio Neto</th>
                          <th className="px-4 py-2 text-right border-b">% Impuestos</th>
                          <th className="px-4 py-2 text-right border-b">Importe</th>
                          <th className="px-4 py-2 text-left border-b">Vencimiento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proyeccionCalculada.liquidaciones_modelo130.map((liq, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">{liq.periodo}</td>
                            <td className="px-4 py-2 text-right">{formatNumberES(liq.beneficio_neto)}€</td>
                            <td className="px-4 py-2 text-right">{liq.porcentaje}%</td>
                            <td className="px-4 py-2 text-right font-semibold text-orange-600">{formatNumberES(liq.importe)}€</td>
                            <td className="px-4 py-2">{new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Verificación de flujo de caja vs beneficio neto */}
              {proyeccionCalculada.totales.iva_repercutido_total !== undefined && (
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <h3 className="text-sm font-semibold text-blue-900 mb-3">📊 Verificación Flujo de Caja</h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-blue-800">Beneficio Antes de Impuestos:</span>
                      <span className="font-medium">{formatNumberES(proyeccionCalculada.totales.beneficio_antes_impuestos_total || proyeccionCalculada.totales.beneficio_neto_total + proyeccionCalculada.totales.impuestos_directos_total)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-800">+ IVA Repercutido (en cobros):</span>
                      <span className="font-medium text-green-600">+{formatNumberES(proyeccionCalculada.totales.iva_repercutido_total)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-800 text-xs italic">IVA Soportado (ya incluido en pagos):</span>
                      <span className="font-medium text-gray-500 text-xs">-{formatNumberES(proyeccionCalculada.totales.iva_soportado_total)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-800">- Liquidaciones Pagadas:</span>
                      <span className="font-medium text-red-600">-{formatNumberES(proyeccionCalculada.totales.liquidaciones_pagadas_total || 0)}€</span>
                    </div>
                    <div className="pt-2 border-t border-blue-300 mt-2">
                      <div className="text-xs font-semibold text-blue-900 mb-1">Cobros/Pagos Pendientes:</div>
                      <div className="flex justify-between">
                        <span className="text-blue-800">- Cobros Pendientes Clientes:</span>
                        <span className="font-medium text-red-600">-{formatNumberES(proyeccionCalculada.totales.cobros_pendientes_clientes || 0)}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-800">+ Pagos Pendientes Proveedores:</span>
                        <span className="font-medium text-green-600">+{formatNumberES(proyeccionCalculada.totales.pagos_pendientes_proveedores || 0)}€</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-blue-300 mt-2 flex justify-between">
                      <span className="font-semibold text-blue-900">Flujo Caja Esperado:</span>
                      <span className="font-bold text-blue-900">
                        {formatNumberES(
                          (proyeccionCalculada.totales.beneficio_antes_impuestos_total || (proyeccionCalculada.totales.beneficio_neto_total + proyeccionCalculada.totales.impuestos_directos_total)) + 
                          proyeccionCalculada.totales.iva_repercutido_total - 
                          (proyeccionCalculada.totales.liquidaciones_pagadas_total || 0) -
                          (proyeccionCalculada.totales.cobros_pendientes_clientes || 0) +
                          (proyeccionCalculada.totales.pagos_pendientes_proveedores || 0)
                        )}€
                      </span>
                    </div>
                    <div className="text-xs text-blue-700 italic mt-1">
                      Nota: El IVA Soportado no deducible ya está incluido en los pagos (se paga con las facturas), por lo que no se resta del flujo de caja.
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-blue-900">Flujo Caja Real:</span>
                      <span className="font-bold text-blue-900">
                        {formatNumberES(proyeccionCalculada.totales.flujo_caja_total)}€
                      </span>
                    </div>
                    {Math.abs(
                      proyeccionCalculada.totales.flujo_caja_total - 
                      ((proyeccionCalculada.totales.beneficio_antes_impuestos_total || (proyeccionCalculada.totales.beneficio_neto_total + proyeccionCalculada.totales.impuestos_directos_total)) + 
                       proyeccionCalculada.totales.iva_repercutido_total - 
                       (proyeccionCalculada.totales.liquidaciones_pagadas_total || 0) -
                       (proyeccionCalculada.totales.cobros_pendientes_clientes || 0) +
                       (proyeccionCalculada.totales.pagos_pendientes_proveedores || 0))
                    ) > 0.01 && (
                      <div className="pt-2 border-t border-red-300 mt-2">
                        <span className="text-xs text-red-700 font-semibold">
                          ⚠️ Diferencia: {formatNumberES(
                            Math.abs(
                              proyeccionCalculada.totales.flujo_caja_total - 
                              ((proyeccionCalculada.totales.beneficio_antes_impuestos_total || (proyeccionCalculada.totales.beneficio_neto_total + proyeccionCalculada.totales.impuestos_directos_total)) + 
                               proyeccionCalculada.totales.iva_repercutido_total - 
                               (proyeccionCalculada.totales.liquidaciones_pagadas_total || 0) -
                               (proyeccionCalculada.totales.cobros_pendientes_clientes || 0) +
                               (proyeccionCalculada.totales.pagos_pendientes_proveedores || 0))
                            )
                          )}€
                        </span>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Información de liquidaciones pendientes */}
              {(proyeccionCalculada.totales.liquidaciones_pendientes_iva?.length > 0 || 
                proyeccionCalculada.totales.liquidaciones_pendientes_130?.length > 0) && (
                <Card className="p-4 bg-yellow-50 border-yellow-200">
                  <h3 className="text-sm font-semibold text-yellow-900 mb-2">⚠️ Liquidaciones Pendientes de Pago</h3>
                  <p className="text-xs text-yellow-800 mb-3">
                    Las siguientes liquidaciones vencen después del período de la proyección y no están incluidas en el flujo de caja:
                  </p>
                  <div className="space-y-2">
                    {proyeccionCalculada.totales.liquidaciones_pendientes_iva?.map((liq, idx) => (
                      <div key={`iva-${idx}`} className="text-xs text-yellow-800">
                        <span className="font-medium">IVA T{liq.trimestre} {liq.año}:</span> {formatNumberES(liq.importe)}€ 
                        <span className="text-yellow-700"> (Vence: {new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')})</span>
                      </div>
                    ))}
                    {proyeccionCalculada.totales.liquidaciones_pendientes_130?.map((liq, idx) => (
                      <div key={`130-${idx}`} className="text-xs text-yellow-800">
                        <span className="font-medium">Modelo 130 {liq.periodo}:</span> {formatNumberES(liq.importe)}€ 
                        <span className="text-yellow-700"> (Vence: {new Date(liq.fecha_vencimiento).toLocaleDateString('es-ES')})</span>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-yellow-300 mt-2">
                      <span className="text-xs font-semibold text-yellow-900">Total Pendiente: </span>
                      <span className="text-xs font-bold text-yellow-900">
                        {formatNumberES(
                          (proyeccionCalculada.totales.liquidaciones_pendientes_iva || []).reduce((sum, liq) => sum + liq.importe, 0) +
                          (proyeccionCalculada.totales.liquidaciones_pendientes_130 || []).reduce((sum, liq) => sum + liq.importe, 0)
                        )}€
                      </span>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal para comparar proyecciones */}
      <Dialog open={compararModalOpen} onOpenChange={setCompararModalOpen}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle>Comparar Proyecciones</DialogTitle>
              <DialogDescription>
                Compara múltiples proyecciones lado a lado
              </DialogDescription>
            </div>
            {proyeccionesComparadas.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportarComparacionPDF}
                  className="flex items-center gap-2"
                >
                  <FileText size={16} />
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportarComparacionExcel}
                  className="flex items-center gap-2"
                >
                  <FileSpreadsheet size={16} />
                  Excel
                </Button>
              </div>
            )}
          </DialogHeader>

          {proyeccionesComparadas.length === 0 ? (
            <div className="py-8">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona al menos 2 proyecciones para comparar:
                </p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {proyecciones.map((proyeccion) => (
                    <div
                      key={proyeccion.id}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleSeleccionProyeccion(proyeccion.id)}
                    >
                      {proyeccionesSeleccionadas.includes(proyeccion.id) ? (
                        <CheckSquare size={20} className="text-blue-600" />
                      ) : (
                        <Square size={20} className="text-gray-400" />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold">{proyeccion.nombre}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(proyeccion.fecha_inicio).toLocaleDateString('es-ES')} - {proyeccion.meses_proyectados} meses
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={cerrarCompararModal}>
                  Cancelar
                </Button>
                <Button onClick={compararProyecciones} disabled={proyeccionesSeleccionadas.length < 2 || comparando}>
                  {comparando ? 'Calculando...' : 'Comparar'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Tabla comparativa de métricas */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp size={20} className="text-blue-600" />
                  Resumen Comparativo
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left border-b">Métrica</th>
                        {proyeccionesComparadas.map((p, idx) => (
                          <th key={idx} className="px-4 py-2 text-right border-b" style={{ color: COLORS[idx % COLORS.length] }}>
                            {p.proyeccion.nombre}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">Ingresos Total</td>
                        {proyeccionesComparadas.map((p, idx) => (
                          <td key={idx} className="px-4 py-2 text-right text-green-600">
                            {formatNumberES(p.totales.ingresos_total)}€
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">Costes Total</td>
                        {proyeccionesComparadas.map((p, idx) => (
                          <td key={idx} className="px-4 py-2 text-right text-red-600">
                            {formatNumberES(p.totales.costes_variables_total + p.totales.gastos_fijos_total)}€
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">Beneficio Neto</td>
                        {proyeccionesComparadas.map((p, idx) => (
                          <td key={idx} className="px-4 py-2 text-right text-purple-600">
                            {formatNumberES(p.totales.beneficio_neto_total)}€
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">Flujo Caja Total</td>
                        {proyeccionesComparadas.map((p, idx) => (
                          <td key={idx} className="px-4 py-2 text-right text-blue-600">
                            {formatNumberES(p.totales.flujo_caja_total)}€
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">Ingresos Promedio Mensual</td>
                        {proyeccionesComparadas.map((p, idx) => {
                          const promedio = p.totales.ingresos_total / (p.proyeccion.meses_proyectados || 1);
                          return (
                            <td key={idx} className="px-4 py-2 text-right">
                              {formatNumberES(promedio)}€
                            </td>
                          );
                        })}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">Beneficio Promedio Mensual</td>
                        {proyeccionesComparadas.map((p, idx) => {
                          const promedio = p.totales.beneficio_neto_total / (p.proyeccion.meses_proyectados || 1);
                          return (
                            <td key={idx} className="px-4 py-2 text-right">
                              {formatNumberES(promedio)}€
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Gráfico comparativo de Beneficios Netos */}
              <Card className="p-6 bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-lg" data-chart="comparacion-beneficios">
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <div className="w-1 h-10 bg-gradient-to-b from-purple-500 to-indigo-600 rounded-full"></div>
                    <span>Comparación de Beneficios Netos</span>
                  </h3>
                  <p className="text-sm text-gray-600 mt-2 ml-4">Evolución mensual del beneficio neto</p>
                </div>
                <ResponsiveContainer width="100%" height={450}>
                  {(() => {
                    // Construir datos de forma simple: usar la primera proyección como base de meses
                    const primeraProyeccion = proyeccionesComparadas[0];
                    if (!primeraProyeccion || !primeraProyeccion.meses) {
                      return null;
                    }
                    
                    // Crear array de datos combinados usando los meses de la primera proyección
                    const datosCombinados = primeraProyeccion.meses.map(m => {
                      const punto = {
                        mes: `${m.nombre_mes || 'Mes'} ${m.año_calendario || ''}`,
                        mesIndex: m.mes - 1,
                        año: m.año_calendario,
                        mesNum: m.mes_calendario
                      };
                      
                      // Agregar datos de cada proyección
                      proyeccionesComparadas.forEach((p, idx) => {
                        // Buscar el mes correspondiente en esta proyección
                        const mesData = p.meses.find(m2 => 
                          m2.mes_calendario === m.mes_calendario && 
                          m2.año_calendario === m.año_calendario
                        );
                        if (mesData) {
                          punto[`proyeccion_${idx}`] = mesData.beneficio_neto;
                        }
                      });
                      
                      return punto;
                    });
                    
                    return (
                      <LineChart data={datosCombinados} margin={{ top: 20, right: 40, left: 20, bottom: 80 }}>
                        <defs>
                          {proyeccionesComparadas.map((p, idx) => {
                            const color = COLORS[idx % COLORS.length];
                            return (
                              <linearGradient key={idx} id={`gradientComp${idx}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.25}/>
                                <stop offset="95%" stopColor={color} stopOpacity={0.02}/>
                              </linearGradient>
                            );
                          })}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="2 2" />
                        <XAxis
                          dataKey="mes"
                          angle={-45}
                          textAnchor="end"
                          height={100}
                          tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                          stroke="#cbd5e1"
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                          tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`}
                          stroke="#cbd5e1"
                          tickLine={false}
                          width={70}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-4 border-2 border-gray-200 rounded-xl shadow-2xl">
                                  <p className="font-bold mb-3 text-base text-gray-900 border-b pb-2">{label}</p>
                                  <div className="space-y-2">
                                    {payload.map((entry, idx) => (
                                      <div key={idx} className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-2">
                                          <div 
                                            className="w-3 h-3 rounded-full" 
                                            style={{ backgroundColor: entry.color }}
                                          ></div>
                                          <span className="text-sm font-medium text-gray-700">{entry.name}</span>
                                        </div>
                                        <span className="text-sm font-bold" style={{ color: entry.color }}>
                                          {formatNumberES(entry.value)}€
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend
                          wrapperStyle={{ paddingTop: '30px' }}
                          iconType="line"
                          iconSize={18}
                          align="center"
                          verticalAlign="top"
                          formatter={(value) => (
                            <span style={{ color: '#475569', fontSize: '13px', fontWeight: 600, letterSpacing: '0.025em' }}>
                              {value}
                            </span>
                          )}
                        />
                        {proyeccionesComparadas.map((p, idx) => {
                          const color = COLORS[idx % COLORS.length];
                          return (
                            <React.Fragment key={idx}>
                              <Area
                                type="monotone"
                                dataKey={`proyeccion_${idx}`}
                                fill={`url(#gradientComp${idx})`}
                                stroke="none"
                                hide={true}
                              />
                              <Line
                                type="monotone"
                                dataKey={`proyeccion_${idx}`}
                                stroke={color}
                                strokeWidth={3.5}
                                dot={false}
                                activeDot={{ r: 8, stroke: color, strokeWidth: 3, fill: '#ffffff', shadow: true }}
                                name={p.proyeccion.nombre}
                              />
                            </React.Fragment>
                          );
                        })}
                      </LineChart>
                    );
                  })()}
                </ResponsiveContainer>
              </Card>

              {/* Gráfico comparativo de tesorería */}
              <Card className="p-6 bg-gradient-to-br from-white to-blue-50 border border-gray-200 shadow-lg" data-chart="comparacion-tesoreria">
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <div className="w-1 h-10 bg-gradient-to-b from-blue-500 to-cyan-600 rounded-full"></div>
                    <span>Comparación de Tesorería Acumulada</span>
                  </h3>
                  <p className="text-sm text-gray-600 mt-2 ml-4">Evolución del saldo acumulado mes a mes</p>
                </div>
                <ResponsiveContainer width="100%" height={450}>
                  {(() => {
                    // Construir datos de forma simple: usar la primera proyección como base de meses
                    const primeraProyeccion = proyeccionesComparadas[0];
                    if (!primeraProyeccion || !primeraProyeccion.tesoreria) {
                      return null;
                    }
                    
                    // Crear array de datos combinados usando los meses de la primera proyección
                    const datosCombinados = primeraProyeccion.tesoreria.map(t => {
                      const punto = {
                        mes: `${t.nombre_mes || 'Mes'} ${t.año_calendario || ''}`,
                        mesIndex: t.mes - 1,
                        año: t.año_calendario,
                        mesNum: t.mes_calendario
                      };
                      
                      // Agregar datos de cada proyección
                      proyeccionesComparadas.forEach((p, idx) => {
                        // Buscar el mes correspondiente en esta proyección
                        const mesData = p.tesoreria.find(t2 => 
                          t2.mes_calendario === t.mes_calendario && 
                          t2.año_calendario === t.año_calendario
                        );
                        if (mesData) {
                          punto[`proyeccion_${idx}`] = mesData.saldo_acumulado;
                        }
                      });
                      
                      return punto;
                    });
                    
                    return (
                      <LineChart data={datosCombinados} margin={{ top: 20, right: 40, left: 20, bottom: 80 }}>
                        <defs>
                          {proyeccionesComparadas.map((p, idx) => {
                            const color = COLORS[idx % COLORS.length];
                            return (
                              <linearGradient key={idx} id={`gradientTesoreria${idx}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.25}/>
                                <stop offset="95%" stopColor={color} stopOpacity={0.02}/>
                              </linearGradient>
                            );
                          })}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="2 2" />
                        <XAxis
                          dataKey="mes"
                          angle={-45}
                          textAnchor="end"
                          height={100}
                          tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                          stroke="#cbd5e1"
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                          tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`}
                          stroke="#cbd5e1"
                          tickLine={false}
                          width={70}
                        />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-4 border-2 border-gray-200 rounded-xl shadow-2xl">
                                  <p className="font-bold mb-3 text-base text-gray-900 border-b pb-2">{label}</p>
                                  <div className="space-y-2">
                                    {payload.map((entry, idx) => (
                                      <div key={idx} className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-2">
                                          <div 
                                            className="w-3 h-3 rounded-full" 
                                            style={{ backgroundColor: entry.color }}
                                          ></div>
                                          <span className="text-sm font-medium text-gray-700">{entry.name}</span>
                                        </div>
                                        <span className="text-sm font-bold" style={{ color: entry.color }}>
                                          {formatNumberES(entry.value)}€
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend
                          wrapperStyle={{ paddingTop: '30px' }}
                          iconType="line"
                          iconSize={18}
                          align="center"
                          verticalAlign="top"
                          formatter={(value) => (
                            <span style={{ color: '#475569', fontSize: '13px', fontWeight: 600, letterSpacing: '0.025em' }}>
                              {value}
                            </span>
                          )}
                        />
                        {proyeccionesComparadas.map((p, idx) => {
                          const color = COLORS[idx % COLORS.length];
                          return (
                            <React.Fragment key={idx}>
                              <Area
                                type="monotone"
                                dataKey={`proyeccion_${idx}`}
                                fill={`url(#gradientTesoreria${idx})`}
                                stroke="none"
                                hide={true}
                              />
                              <Line
                                type="monotone"
                                dataKey={`proyeccion_${idx}`}
                                stroke={color}
                                strokeWidth={3.5}
                                dot={false}
                                activeDot={{ r: 8, stroke: color, strokeWidth: 3, fill: '#ffffff', shadow: true }}
                                name={p.proyeccion.nombre}
                              />
                            </React.Fragment>
                          );
                        })}
                      </LineChart>
                    );
                  })()}
                </ResponsiveContainer>
              </Card>

              {/* Tabla comparativa de unidades por producto */}
              {productos.length > 0 && (
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp size={20} className="text-blue-600" />
                    Comparación de Unidades por Producto (Total Anual)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left border-b">Producto</th>
                          {proyeccionesComparadas.map((p, idx) => (
                            <th key={idx} colSpan={3} className="px-4 py-2 text-center border-b" style={{ color: COLORS[idx % COLORS.length] }}>
                              {p.proyeccion.nombre}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          <th className="px-4 py-2 text-left border-b"></th>
                          {proyeccionesComparadas.map((p, idx) => (
                            <React.Fragment key={idx}>
                              <th className="px-4 py-2 text-right border-b text-xs text-gray-600">Total Anual</th>
                              <th className="px-4 py-2 text-right border-b text-xs text-gray-600">Media/Mes</th>
                              <th className="px-4 py-2 text-right border-b text-xs text-gray-600">Horas/Mes</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {productos.map((producto) => {
                          // Calcular total de unidades, media mensual y horas por proyección
                          const datosPorProyeccion = proyeccionesComparadas.map(p => {
                            const unidadesPorMes = p.proyeccion?.unidades_por_mes || {};
                            const unidades = unidadesPorMes[producto.id] || [];
                            const total = Array.isArray(unidades) 
                              ? unidades.reduce((sum, u) => sum + (parseFloat(u) || 0), 0)
                              : 0;
                            const mesesProyectados = p.proyeccion.meses_proyectados || 1;
                            const mediaMensual = total / mesesProyectados;
                            
                            // Calcular horas: duracion_minutos + tiempo_preparacion según tipo
                            const duracionHoras = (producto.duracion_minutos || 0) / 60.0;
                            const tiempoPrepHoras = (producto.tiempo_preparacion_minutos || 0) / 60.0;
                            const tipoPrep = producto.tipo_preparacion || 'por_imparticion';
                            
                            let horasPorUnidad = duracionHoras;
                            if (tipoPrep === 'unico') {
                              // Preparación única: se suma una vez al total, no por unidad
                              horasPorUnidad = duracionHoras;
                            } else {
                              // Preparación por impartición: se suma a cada unidad
                              horasPorUnidad = duracionHoras + tiempoPrepHoras;
                            }
                            
                            const horasTotales = total * horasPorUnidad;
                            const horasMensuales = horasTotales / mesesProyectados;
                            
                            // Si es preparación única, sumar el tiempo de preparación una vez
                            if (tipoPrep === 'unico' && total > 0) {
                              const horasTotalesConPrep = horasTotales + tiempoPrepHoras;
                              const horasMensualesConPrep = horasTotalesConPrep / mesesProyectados;
                              return {
                                total,
                                mediaMensual,
                                horasMensuales: horasMensualesConPrep
                              };
                            }
                            
                            return {
                              total,
                              mediaMensual,
                              horasMensuales
                            };
                          });
                          
                          return (
                            <tr key={producto.id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">{producto.nombre}</td>
                              {datosPorProyeccion.map((datos, idx) => (
                                <React.Fragment key={idx}>
                                  <td className="px-4 py-2 text-right">
                                    {formatNumberES(datos.total)} unidades
                                  </td>
                                  <td className="px-4 py-2 text-right text-gray-600">
                                    {formatNumberES(datos.mediaMensual)} unidades
                                  </td>
                                  <td className="px-4 py-2 text-right text-blue-600">
                                    {formatNumberES(datos.horasMensuales)} horas
                                  </td>
                                </React.Fragment>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Tabla comparativa de costes */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <DollarSign size={20} className="text-green-600" />
                  Comparación de Costes
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left border-b">Tipo de Coste</th>
                        {proyeccionesComparadas.map((p, idx) => (
                          <th key={idx} className="px-4 py-2 text-right border-b" style={{ color: COLORS[idx % COLORS.length] }}>
                            {p.proyeccion.nombre}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Costes Variables - Resumen */}
                      <tr className="border-b hover:bg-gray-50 bg-orange-50">
                        <td className="px-4 py-2 font-semibold text-orange-700">Costes Variables Total</td>
                        {proyeccionesComparadas.map((p, idx) => (
                          <td key={idx} className="px-4 py-2 text-right font-semibold text-orange-700">
                            {formatNumberES(p.totales.costes_variables_total)}€
                          </td>
                        ))}
                      </tr>
                      
                      {/* Desglose de Costes Variables */}
                      {(() => {
                        // Recopilar todos los costes variables únicos de todos los productos
                        const costesVariablesMap = new Map();
                        proyeccionesComparadas.forEach((p, pIdx) => {
                          const productosProyeccion = p.productos || [];
                          const unidadesPorMes = p.proyeccion?.unidades_por_mes || {};
                          
                          productosProyeccion.forEach(producto => {
                            const unidades = unidadesPorMes[producto.id] || [];
                            const totalUnidades = Array.isArray(unidades) 
                              ? unidades.reduce((sum, u) => sum + (parseFloat(u) || 0), 0)
                              : 0;
                            
                            if (totalUnidades > 0 && producto.costes_variables && Array.isArray(producto.costes_variables)) {
                              producto.costes_variables.forEach(costo => {
                                const clave = `${costo.nombre}_${costo.tipo}`;
                                if (!costesVariablesMap.has(clave)) {
                                  costesVariablesMap.set(clave, {
                                    nombre: costo.nombre,
                                    tipo: costo.tipo,
                                    valores: new Array(proyeccionesComparadas.length).fill(0)
                                  });
                                }
                                
                                // Calcular el coste según el tipo (igual que en el backend: calcular_costes_con_iva)
                                let costeTotal = 0;
                                const valor = parseFloat(costo.valor) || 0;
                                
                                if (costo.tipo === 'fijo') {
                                  costeTotal = valor * totalUnidades;
                                } else if (costo.tipo === 'por_hora') {
                                  // Igual que backend línea 171: solo duracion_minutos, NO tiempo_preparacion
                                  const duracionMinutos = parseFloat(producto.duracion_minutos) || 60;
                                  const horasPorUnidad = duracionMinutos / 60.0;
                                  costeTotal = valor * horasPorUnidad * totalUnidades;
                                } else if (costo.tipo === 'porcentaje') {
                                  const precioUnitario = (parseFloat(producto.precio) || 0) * (parseInt(producto.max_participantes) || 1);
                                  costeTotal = (precioUnitario * valor / 100.0) * totalUnidades;
                                }
                                
                                const costesMap = costesVariablesMap.get(clave);
                                costesMap.valores[pIdx] += costeTotal;
                              });
                            }
                          });
                        });
                        
                        return Array.from(costesVariablesMap.values())
                          .filter(costo => costo.valores.some(v => Math.abs(v) > 0.01)) // Solo mostrar costes con valores > 0.01€
                          .sort((a, b) => {
                            // Ordenar por nombre para consistencia
                            return a.nombre.localeCompare(b.nombre);
                          })
                          .map((costo, idx) => (
                            <tr key={`cv-${idx}`} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 pl-8 text-gray-600 text-xs">
                                • {costo.nombre} ({costo.tipo === 'fijo' ? 'fijo' : costo.tipo === 'por_hora' ? 'por hora' : 'porcentaje'})
                              </td>
                              {costo.valores.map((valor, vIdx) => (
                                <td key={vIdx} className="px-4 py-2 text-right text-gray-600 text-xs">
                                  {formatNumberES(valor)}€
                                </td>
                              ))}
                            </tr>
                          ));
                      })()}
                      
                      {/* Gastos Fijos - Resumen */}
                      <tr className="border-b hover:bg-gray-50 bg-red-50">
                        <td className="px-4 py-2 font-semibold text-red-700">Gastos Fijos Total</td>
                        {proyeccionesComparadas.map((p, idx) => (
                          <td key={idx} className="px-4 py-2 text-right font-semibold text-red-700">
                            {formatNumberES(p.totales.gastos_fijos_total)}€
                          </td>
                        ))}
                      </tr>
                      
                      {/* Desglose de Gastos Fijos */}
                      {(() => {
                        // Usar los gastos fijos de la primera proyección (deberían ser los mismos para todas)
                        const primeraProyeccion = proyeccionesComparadas[0];
                        const gastosFijos = primeraProyeccion?.gastos_fijos || [];
                        
                        return gastosFijos.map((gasto, idx) => {
                          // Calcular el total anual de cada gasto fijo
                          const valoresPorProyeccion = proyeccionesComparadas.map(p => {
                            const gastosFijosProyeccion = p.gastos_fijos || [];
                            const gastoEncontrado = gastosFijosProyeccion.find(g => g.nombre === gasto.nombre);
                            
                            if (!gastoEncontrado) return 0;
                            
                            // El valor puede ser mensual o anual según los meses de pago
                            const mesesPago = gastoEncontrado.meses || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
                            const valor = gastoEncontrado.valor || 0;
                            
                            // Si se paga en 12 meses, el valor es mensual, si no, es anual
                            if (mesesPago.length === 12) {
                              return valor * 12; // Valor mensual * 12 meses
                            } else {
                              return valor; // Valor anual
                            }
                          });
                          
                          return (
                            <tr key={`gf-${idx}`} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 pl-8 text-gray-600 text-xs">
                                • {gasto.nombre}
                              </td>
                              {valoresPorProyeccion.map((valor, vIdx) => (
                                <td key={vIdx} className="px-4 py-2 text-right text-gray-600 text-xs">
                                  {formatNumberES(valor)}€
                                </td>
                              ))}
                            </tr>
                          );
                        });
                      })()}
                      
                      {/* Total Costes */}
                      <tr className="border-b hover:bg-gray-50 bg-gray-50">
                        <td className="px-4 py-2 font-semibold">Total Costes</td>
                        {proyeccionesComparadas.map((p, idx) => (
                          <td key={idx} className="px-4 py-2 text-right font-semibold text-red-700">
                            {formatNumberES(p.totales.costes_variables_total + p.totales.gastos_fijos_total)}€
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">Coste Promedio Mensual</td>
                        {proyeccionesComparadas.map((p, idx) => {
                          const promedio = (p.totales.costes_variables_total + p.totales.gastos_fijos_total) / (p.proyeccion.meses_proyectados || 1);
                          return (
                            <td key={idx} className="px-4 py-2 text-right">
                              {formatNumberES(promedio)}€
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>

              <DialogFooter>
                <Button variant="outline" onClick={cerrarCompararModal}>
                  Cerrar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Proyecciones;

